import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { API_BASE_URL, apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

import { FORM_MODE, EMAIL_MODE, MOBILE_MODE, PPM_QUOTE_MODE, getEmailDefaultContent, getMobileDefaultContent, componentsToEmailHtml, componentsToMobileLayout, calculateEmailZonePositions, EMAIL_ZONES, EMAIL_ZONE_ORDER } from './TemplateModes';

const API_URL = API_BASE_URL;

// Grid size for snapping (pixels)
const GRID_SIZE = 10;

// Paper sizes in pixels (at 96 DPI)
const PAPER_SIZES = {
  a4: { width: 794, height: 1123, label: 'A4 (210 × 297mm)' },
  letter: { width: 816, height: 1056, label: 'Letter (8.5 × 11")' },
  a5: { width: 559, height: 794, label: 'A5 (148 × 210mm)' },
  legal: { width: 816, height: 1344, label: 'Legal (8.5 × 14")' },
  a3: { width: 1123, height: 1587, label: 'A3 (297 × 420mm)' }
};

// Default to A4
const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

// Alignment threshold (pixels)
const ALIGN_THRESHOLD = 5;

// Component types
const COMPONENT_TYPES = [
  { id: 'text', label: 'Text Block', icon: '📝' },
  { id: 'heading', label: 'Heading', icon: '📌' },
  { id: 'variable', label: 'Variable', icon: '🔤' },
  { id: 'data-table', label: 'Data Table', icon: '📋' },
  { id: 'table', label: 'Table (Manual)', icon: '📊' },
  { id: 'image', label: 'Image/Logo', icon: '🖼️' },
  { id: 'line', label: 'Line', icon: '➖' },
  { id: 'spacer', label: 'Spacer', icon: '⬜' },
];

// Available variables by area
const AREA_VARIABLES = {
  sales_order: {
    customer: ['customer_name', 'customer_address', 'customer_po'],
    order: ['order_number', 'order_date', 'expected_dispatch'],
    delivery: ['delivery_address', 'delivery_method', 'delivery_contact', 'delivery_phone', 'delivery_instructions'],
    financial: ['subtotal', 'vat', 'total'],
    special: ['items', 'notes', 'customer_notes'],
    company: ['company_name', 'company_logo', 'company_address', 'company_address_line1', 'company_address_line2', 'company_city', 'company_county', 'company_postcode', 'company_phone', 'company_email', 'company_phone_formatted', 'company_email_formatted']
  },
  quote: {
    customer: ['customer_name', 'customer_email', 'customer_phone', 'customer_address'],
    quote: ['quote_number', 'quote_date', 'valid_until'],
    financial: ['subtotal', 'vat_amount', 'total'],
    special: ['items_table', 'terms'],
    company: ['company_name', 'company_logo', 'company_address', 'company_address_line1', 'company_address_line2', 'company_city', 'company_county', 'company_postcode', 'company_phone', 'company_mobile', 'company_email', 'company_accounts_email', 'company_website', 'company_vat_number', 'company_number', 'company_phone_formatted', 'company_email_formatted', 'company_mobile_formatted']
  },
  invoice: {
    customer: ['customer_name', 'customer_email', 'customer_address'],
    invoice: ['invoice_number', 'invoice_date', 'due_date'],
    financial: ['subtotal', 'vat_amount', 'total'],
    cis: ['is_cis_invoice', 'cis_rate', 'cis_deduction', 'cis_net_payment'],
    reverse_vat: ['is_reverse_vat', 'reverse_vat_note'],
    special: ['items_table', 'payment_terms', 'company_legal_wording'],
    company: ['company_name', 'company_logo', 'company_address', 'company_address_line1', 'company_address_line2', 'company_city', 'company_county', 'company_postcode', 'company_phone', 'company_mobile', 'company_email', 'company_accounts_email', 'company_website', 'company_vat_number', 'company_number', 'company_phone_formatted', 'company_email_formatted', 'company_mobile_formatted'],
    bank: ['bank_name', 'bank_account_name', 'bank_sort_code', 'bank_account_number', 'bank_iban', 'bank_swift', 'bank_details', 'bank_remittance_email'],
    ecap: ['ecap_bank_name', 'ecap_account_name', 'ecap_sort_code', 'ecap_account_number', 'ecap_iban', 'ecap_swift', 'ecap_bank_details', 'ecap_remittance_email', 'ecap_legal_text']
  },
  job: {
    customer: ['customer_name'],
    site: ['site_name', 'site_address'],
    job: ['job_number', 'job_date', 'status'],
    special: ['notes'],
    company: ['company_name', 'company_logo', 'company_address', 'company_address_line1', 'company_address_line2', 'company_city', 'company_county', 'company_postcode', 'company_phone', 'company_mobile', 'company_email', 'company_accounts_email', 'company_website', 'company_vat_number', 'company_number', 'company_phone_formatted', 'company_email_formatted', 'company_mobile_formatted']
  },
  ppm_quote: {
    customer: ['customer_name', 'contact_name', 'site_address', 'customer_email', 'customer_phone', 'customer_uuid'],
    quote: ['quote_ref', 'quote_date', 'variant', 'contract_length', 'visits_per_year', 'valid_until', 'quote_status'],
    assets: ['auto_doors', 'manual_doors', 'gates', 'barriers', 'shutters', 'total_assets', 'asset_details'],
    pricing: ['per_visit', 'annual_value', 'contract_value', 'discount_percent', 'subtotal', 'vat_amount', 'total', 'payment_terms'],
    callout: ['callout_standard', 'callout_response', 'callout_emergency', 'callout_out_of_hours'],
    company: ['company_name', 'company_logo', 'company_address', 'company_address_line1', 'company_address_line2', 'company_city', 'company_county', 'company_postcode', 'company_phone', 'company_mobile', 'company_email', 'company_website', 'company_vat_number', 'company_number', 'company_phone_formatted', 'company_email_formatted', 'company_mobile_formatted'],
    dates: ['today_date', 'contract_start', 'contract_end', 'next_visit_date'],
    special: ['feature_image', 'intro_heading', 'intro_body', 'about_body', 'scope_body', 'scope_items', 'terms_list', 'signoff_text', 'signoff_name', 'signoff_dept', 'page_number', 'total_pages']
  }
};

// Sample data for variable preview
const getSampleData = (variable) => {
  const samples = {
    // Company
    company_name: 'DoorBase Ltd',
    company_address: 'Unit 5, Industrial Estate\nHarwich\nEssex\nCO12 3AB',
    company_address_line1: 'Unit 5, Industrial Estate',
    company_address_line2: '',
    company_city: 'Harwich',
    company_county: 'Essex',
    company_postcode: 'CO12 3AB',
    company_phone: '01255 123456',
    company_mobile: '07700 900123',
    company_email: 'info@doorbase.co.uk',
    company_phone_formatted: 'Tel: 01255 123456',
    company_mobile_formatted: 'Mobile: 07700 900123',
    company_email_formatted: 'Email: info@doorbase.co.uk',

    // Bank Details (Our Bank)
    bank_name: 'Barclays Bank PLC',
    bank_account_name: 'DoorBase Ltd',
    bank_sort_code: '20-00-00',
    bank_account_number: '12345678',
    bank_iban: 'GB29 NWBK 6016 1331 9268 19',
    bank_swift: 'BARCGB22',
    bank_details: 'Bank: Barclays Bank PLC\nAccount Name: DoorBase Ltd\nSort Code: 20-00-00\nAccount Number: 12345678\nIBAN: GB29 NWBK 6016 1331 9268 19',
    bank_remittance_email: 'accounts@doorbase.co.uk',

    // eCap Bank Details
    ecap_bank_name: 'eCap Finance Bank',
    ecap_account_name: 'DoorBase Ltd - eCap Account',
    ecap_sort_code: '30-00-00',
    ecap_account_number: '87654321',
    ecap_iban: 'GB50 NWBK 3000 0087 6543 21',
    ecap_swift: 'ECAPGB22',
    ecap_bank_details: 'Bank: eCap Finance Bank\nAccount Name: DoorBase Ltd - eCap Account\nSort Code: 30-00-00\nAccount Number: 87654321',
    ecap_remittance_email: 'remittance@ecap.com',
    ecap_legal_text: 'This invoice has been assigned to eCap Finance Ltd. Please make payment to the account details shown above.',

    // Legal Wording
    company_legal_wording: 'Payment terms: Net 30 days from invoice date. Late payment may incur interest charges. Title of goods remains with DoorBase Ltd until payment is received in full. All disputes subject to UK law.',

    // Customer
    customer_name: 'ABC Manufacturing Ltd',
    customer_address: '123 High Street\nLondon\nSW1A 1AA',
    customer_email: 'contact@abcmfg.co.uk',
    customer_phone: '020 7123 4567',
    customer_po: 'PO-2024-001',

    // Order/Quote/Invoice
    order_number: 'SO-2024-001',
    order_date: '22 Apr 2026',
    quote_number: 'QT-2024-001',
    quote_date: '22 Apr 2026',
    invoice_number: 'INV-2024-001',
    invoice_date: '22 Apr 2026',
    due_date: '22 May 2026',
    valid_until: '22 May 2026',
    expected_dispatch: '25 Apr 2026',

    // Financial
    subtotal: '£1,250.00',
    vat: '£250.00',
    vat_amount: '£250.00',
    total: '£1,500.00',

    // Delivery
    delivery_address: '123 High Street\nLondon\nSW1A 1AA',
    delivery_method: 'Next Day Delivery',
    delivery_contact: 'John Smith',
    delivery_phone: '020 7123 4567',
    delivery_instructions: 'Please deliver to goods entrance',

    // Site/Job
    site_name: 'Main Office Building',
    site_address: '123 High Street\nLondon\nSW1A 1AA',
    job_number: 'JOB-2024-001',
    job_date: '22 Apr 2026',
    status: 'In Progress',

    // Special
    notes: 'Additional notes here',
    customer_notes: 'Customer requirements',
    terms: 'Payment terms: 30 days net',
    payment_terms: 'Payment due within 30 days'
  };

  return samples[variable] || `{{${variable}}}`;
};

// Snap to grid (now takes size parameter)
const snapToGrid = (x, y, size = GRID_SIZE) => {
  // Pixel-perfect snapping to exact grid intersections
  // Use Math.round to snap to nearest grid line (not floor/ceil which bias direction)
  const snappedX = Math.round(x / size) * size;
  const snappedY = Math.round(y / size) * size;

  // Ensure integer pixels (no sub-pixel positioning)
  return {
    x: Math.round(snappedX),
    y: Math.round(snappedY)
  };
};

// Find alignment guides
const findAlignmentGuides = (component, allComponents) => {
  const guides = [];
  const threshold = ALIGN_THRESHOLD;

  allComponents.forEach(other => {
    if (other.id === component.id) return;

    const compRight = component.x + (component.width || 200);
    const compBottom = component.y + (component.height || 50);
    const otherRight = other.x + (other.width || 200);
    const otherBottom = other.y + (other.height || 50);

    // Vertical guides
    if (Math.abs(component.x - other.x) < threshold) {
      guides.push({ type: 'vertical', pos: other.x });
    }
    if (Math.abs(compRight - otherRight) < threshold) {
      guides.push({ type: 'vertical', pos: otherRight });
    }

    // Horizontal guides
    if (Math.abs(component.y - other.y) < threshold) {
      guides.push({ type: 'horizontal', pos: other.y });
    }
    if (Math.abs(compBottom - otherBottom) < threshold) {
      guides.push({ type: 'horizontal', pos: otherBottom });
    }
  });

  return guides;
};

// Apply alignment snapping to other components
const applyAlignmentSnap = (x, y, width, height, allComponents, currentId) => {
  const threshold = ALIGN_THRESHOLD;
  let snappedX = x;
  let snappedY = y;

  const compRight = x + (width || 200);
  const compBottom = y + (height || 50);

  allComponents.forEach(other => {
    if (other.id === currentId) return;

    const otherRight = other.x + (other.width || 200);
    const otherBottom = other.y + (other.height || 50);

    // Snap left edge to other's left edge
    if (Math.abs(x - other.x) < threshold) snappedX = other.x;

    // Snap right edge to other's right edge
    if (Math.abs(compRight - otherRight) < threshold) snappedX = otherRight - (width || 200);

    // Snap top edge to other's top edge
    if (Math.abs(y - other.y) < threshold) snappedY = other.y;

    // Snap bottom edge to other's bottom edge
    if (Math.abs(compBottom - otherBottom) < threshold) snappedY = otherBottom - (height || 50);
  });

  // Ensure integer pixels for pixel-perfect positioning
  return {
    x: Math.round(snappedX),
    y: Math.round(snappedY)
  };
};

function PaletteItem({ type }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'NEW_COMPONENT',
    item: { componentType: type.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }));

  return (
    <button
      ref={drag}
      className={`p-3 border-2 border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition flex items-center space-x-2 cursor-move ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <span className="text-xl">{type.icon}</span>
      <span className="font-medium text-sm">{type.label}</span>
    </button>
  );
}

function CanvasComponent({ component, isSelected, isMultiSelected, selectedIds, onSelect, onUpdate, onDelete, zoom, showBackgrounds, ctrlPressed }) {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeData, setResizeData] = useState(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'EXISTING_COMPONENT',
    item: {
      id: component.id,
      startX: component.x,
      startY: component.y,
      isMultiSelect: selectedIds && selectedIds.length > 1,
      selectedIds: selectedIds || []
    },
    canDrag: !component.locked,
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  }), [component.id, component.x, component.y, component.locked, selectedIds]);

  useEffect(() => {
    if (!isResizing || !resizeData) return;

    const handleMove = (e) => {
      const dx = e.clientX - resizeData.startX;
      const dy = e.clientY - resizeData.startY;

      let updates = {};

      if (resizeData.handle.includes('e')) {
        updates.width = Math.max(50, resizeData.width + dx);
      }
      if (resizeData.handle.includes('w')) {
        const newWidth = Math.max(50, resizeData.width - dx);
        updates.width = newWidth;
        updates.x = resizeData.x + (resizeData.width - newWidth);
      }
      if (resizeData.handle.includes('s')) {
        updates.height = Math.max(20, resizeData.height + dy);
      }
      if (resizeData.handle.includes('n')) {
        const newHeight = Math.max(20, resizeData.height - dy);
        updates.height = newHeight;
        updates.y = resizeData.y + (resizeData.height - newHeight);
      }

      onUpdate(component.id, updates);
    };

    const handleUp = () => {
      setIsResizing(false);
      setResizeData(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isResizing, resizeData, component.id, onUpdate]);

  const startResize = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeData({
      handle,
      startX: e.clientX,
      startY: e.clientY,
      x: component.x,
      y: component.y,
      width: component.width || 200,
      height: component.height || 50
    });
  };

  const renderContent = () => {
    const verticalAlign = component.verticalAlign || 'top';
    const flexAlign = verticalAlign === 'bottom' ? 'flex-end' : verticalAlign === 'top' ? 'flex-start' : 'center';

    const style = {
      fontSize: component.fontSize || 12,
      fontFamily: component.fontFamily || 'Arial',
      fontWeight: component.fontWeight || 'normal',
      fontStyle: component.fontStyle || 'normal',
      textDecoration: component.textDecoration || 'none',
      textAlign: component.textAlign || 'left',
      lineHeight: component.lineHeight || 1.2,
      letterSpacing: `${component.letterSpacing || 0}px`,
      color: component.color || '#000000',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: flexAlign
    };

    const paddingX = component.paddingX !== undefined ? component.paddingX : 4;
    const paddingY = component.paddingY !== undefined ? component.paddingY : 4;

    switch (component.type) {
      case 'text':
      case 'heading':
        return (
          <div style={style} className="pointer-events-none">
            <div style={{ textAlign: component.textAlign || 'left', padding: `${paddingY}px ${paddingX}px` }}>
              {component.content || 'Text'}
            </div>
          </div>
        );
      case 'variable':
        const sampleText = component.variable ? getSampleData(component.variable) : 'Variable';
        return (
          <div style={style} className="pointer-events-none">
            <div className="whitespace-pre-wrap opacity-70" style={{ textAlign: component.textAlign || 'left', padding: `${paddingY}px ${paddingX}px` }}>
              {sampleText}
            </div>
          </div>
        );
      case 'table':
        const tableData = component.tableData || [['Cell']];
        const headerBg = component.tableHeaderBgColor || '#f3f4f6';
        const headerText = component.tableHeaderTextColor || '#000000';
        const cellStyles = component.tableCellStyles || {};

        const getCellStyle = (rowIdx, colIdx) => {
          const key = `${rowIdx}-${colIdx}`;
          const custom = cellStyles[key] || {};
          const isHeader = rowIdx === 0;

          return {
            fontSize: `${custom.fontSize || 10}px`,
            fontWeight: custom.fontWeight || (isHeader ? 'bold' : 'normal'),
            fontStyle: custom.fontStyle || 'normal',
            textAlign: custom.textAlign || 'left',
            color: custom.color || (isHeader ? headerText : '#000000'),
            backgroundColor: custom.backgroundColor || (isHeader ? headerBg : 'transparent')
          };
        };

        return (
          <table className="w-full text-[10px] border-collapse pointer-events-none border">
            <tbody>
              {tableData.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, colIdx) => (
                    <td
                      key={colIdx}
                      className="border px-2 py-1"
                      style={getCellStyle(rowIdx, colIdx)}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      case 'data-table':
        const preset = component.dataTablePreset || 'parts-only';
        const dtHeaderBg = component.tableHeaderBgColor || '#2563eb';
        const dtHeaderText = component.tableHeaderTextColor || '#ffffff';

        let previewHeaders = [];
        let previewRows = [];

        if (preset === 'parts-only') {
          previewHeaders = ['Part No', 'Description', 'Qty', 'Price', 'Total'];
          previewRows = [
            ['ABC123', 'Sample Product', '2', '£50.00', '£100.00'],
            ['XYZ789', 'Another Product', '1', '£75.00', '£75.00']
          ];
        } else if (preset === 'parts-labor') {
          previewHeaders = ['Description', 'Qty/Hours', 'Rate', 'Total'];
          previewRows = [
            ['Sample Product', '2', '£50.00', '£100.00'],
            ['Labor: Installation', '3 hrs', '£40.00', '£120.00']
          ];
        } else if (preset === 'simple-list') {
          previewHeaders = ['Item', 'Details'];
          previewRows = [
            ['Item 1', 'Details here'],
            ['Item 2', 'More details']
          ];
        }

        return (
          <div className="w-full h-full overflow-hidden">
            <table className="w-full text-[10px] border-collapse border">
              <thead>
                <tr style={{ backgroundColor: dtHeaderBg, color: dtHeaderText }}>
                  {previewHeaders.map((header, idx) => (
                    <th key={idx} className="border px-2 py-1 text-left font-bold">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, rowIdx) => (
                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {row.map((cell, colIdx) => (
                      <td key={colIdx} className="border px-2 py-1">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center text-xs text-gray-500 mt-2 italic">
              Preview - Data from {component.variable || 'database'}
            </div>
          </div>
        );
      case 'image':
        if (component.uploadedImage) {
          return (
            <div className="w-full h-full flex items-center justify-center bg-white pointer-events-none" style={{ justifyContent: component.imageAlign === 'left' ? 'flex-start' : component.imageAlign === 'right' ? 'flex-end' : 'center' }}>
              <img
                src={component.uploadedImage}
                alt="Uploaded"
                className="max-w-full max-h-full"
                style={{ width: '100%', height: '100%', objectFit: component.objectFit || 'contain', pointerEvents: 'none' }}
              />
            </div>
          );
        }
        if (component.variable === 'company_logo') {
          return (
            <div className="w-full h-full flex items-center justify-center bg-blue-50 border-2 border-blue-300 pointer-events-none">
              <div className="text-center">
                <div className="text-2xl mb-1">🏢</div>
                <span className="text-blue-600 text-xs font-medium">Company Logo</span>
              </div>
            </div>
          );
        }
        return <div className="w-full h-full flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 pointer-events-none">
          <span className="text-gray-400 text-xs">
            {component.variable ? `{{${component.variable}}}` : 'Drop image or select source'}
          </span>
        </div>;
      case 'line':
        return <hr className="border-t-2 border-gray-400 pointer-events-none" />;
      case 'spacer':
        return <div className="w-full h-full bg-gray-50 pointer-events-none" />;
      // ─── Email mode block types ───
      case 'header': {
        const c = component.contentObj || {};
        const logoOnly = c.logoUrl && !c.companyText && !c.text;
        return (
          <div className="w-full h-full flex items-center pointer-events-none" style={{ backgroundColor: c.bgColor || '#1a365d', color: c.textColor || '#ffffff', padding: '10px 20px' }}>
            {c.logoUrl
              ? <div className="flex items-center w-full" style={{ justifyContent: logoOnly ? 'flex-start' : 'space-between' }}>
                  <img src={c.logoUrl} alt="Logo" style={{ maxHeight: '40px' }} />
                  {(c.companyText || c.text) && <span style={{ fontSize: `${c.fontSize || 22}px`, fontWeight: 'bold', marginLeft: 'auto' }}>{c.companyText || c.text}</span>}
                </div>
              : <span style={{ fontSize: `${c.fontSize || 22}px`, fontWeight: 'bold', textAlign: 'center', width: '100%' }}>{c.text || c.companyText || 'Company Name'}</span>
            }
          </div>
        );
      }
      case 'button': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex items-center justify-center pointer-events-none" style={{ backgroundColor: 'transparent' }}>
            <div style={{
              backgroundColor: c.bgColor || '#e53e3e',
              color: c.textColor || '#ffffff',
              padding: '10px 24px',
              borderRadius: `${c.borderRadius || 4}px`,
              fontSize: `${c.fontSize || 14}px`,
              fontWeight: 'bold',
              textAlign: 'center',
            }}>
              {c.text || 'Click Here'}
            </div>
          </div>
        );
      }
      case 'divider': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex items-center pointer-events-none" style={{ padding: '5px 20px' }}>
            <hr style={{ width: '100%', border: 'none', borderTop: `${c.thickness || 1}px solid ${c.color || '#dddddd'}` }} />
          </div>
        );
      }
      case 'social': {
        const c = component.contentObj || {};
        const icons = { website: '🌐', facebook: '📘', linkedin: '💼', twitter: '🐦', instagram: '📷', youtube: '📺' };
        const links = c.links || [];
        return (
          <div className="w-full h-full flex items-center justify-center gap-4 pointer-events-none" style={{ color: c.color || '#1a365d', fontSize: '14px' }}>
            {links.map((l, i) => (
              <span key={i} className="flex items-center gap-1">
                <span>{icons[l.platform] || '🔗'}</span>
                <span style={{ fontSize: '12px' }}>{l.platform}</span>
              </span>
            ))}
            {links.length === 0 && <span style={{ color: '#999', fontSize: '12px' }}>Add social links</span>}
          </div>
        );
      }
      case 'footer': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex flex-col items-center justify-center pointer-events-none" style={{ backgroundColor: c.bgColor || '#1a365d', color: c.textColor || '#ffffff', padding: '10px 20px', fontSize: `${c.fontSize || 12}px` }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: `${(c.fontSize || 12) + 2}px` }}>{c.company || 'Company Name'}</div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', fontSize: '11px', opacity: 0.85 }}>
              {c.phone && <span>📞 {c.phone}</span>}
              {c.email && <span>✉️ {c.email}</span>}
              {c.website && <span>🌐 {c.website}</span>}
            </div>
            {c.address && <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px' }}>{c.address}</div>}
          </div>
        );
      }
      case '2col': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex pointer-events-none" style={{ gap: `${(c.gap || 20)}px`, padding: '5px 10px' }}>
            <div className="flex-1 flex items-center" style={{ color: c.left?.color || '#333', fontSize: `${c.left?.fontSize || 14}px` }}>
              {c.left?.text || 'Left column'}
            </div>
            <div className="flex-1 flex items-center" style={{ color: c.right?.color || '#333', fontSize: `${c.right?.fontSize || 14}px` }}>
              {c.right?.text || 'Right column'}
            </div>
          </div>
        );
      }
      // ─── Mobile mode block types ───
      case 'input': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex flex-col justify-center pointer-events-none" style={{ padding: '4px 12px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>{c.label || 'Field Label'}{c.required ? ' *' : ''}</label>
            <div style={{ height: '36px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '14px', color: '#9ca3af' }}>
              {c.placeholder || 'Enter value...'}
            </div>
          </div>
        );
      }
      case 'textarea': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex flex-col justify-center pointer-events-none" style={{ padding: '4px 12px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>{c.label || 'Details'}{c.required ? ' *' : ''}</label>
            <div style={{ minHeight: '60px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#f9fafb', padding: '8px 10px', fontSize: '14px', color: '#9ca3af' }}>
              {c.placeholder || 'Enter details...'}
            </div>
          </div>
        );
      }
      case 'select': {
        const c = component.contentObj || {};
        const opts = c.options || ['Option 1', 'Option 2', 'Option 3'];
        return (
          <div className="w-full h-full flex flex-col justify-center pointer-events-none" style={{ padding: '4px 12px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>{c.label || 'Select option'}{c.required ? ' *' : ''}</label>
            <div style={{ height: '36px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', fontSize: '14px', color: '#6b7280' }}>
              <span>{opts[0] || 'Select...'}</span>
              <span style={{ fontSize: '10px' }}>▼</span>
            </div>
          </div>
        );
      }
      case 'checkbox': {
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex items-center pointer-events-none" style={{ padding: '4px 12px' }}>
            <div style={{ width: '20px', height: '20px', border: '2px solid #d1d5db', borderRadius: '4px', backgroundColor: c.checked ? '#1a365d' : '#fff', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {c.checked && <span style={{ color: '#fff', fontSize: '12px', lineHeight: 1 }}>✓</span>}
            </div>
            <span style={{ fontSize: '14px', color: '#374151' }}>{c.label || 'I agree to the terms'}</span>
          </div>
        );
      }
      // ─── PPM Quote mode block types ───
      // These components render as visual previews in the builder canvas.
      // When exported to the SM8 portal, the PPM renderer fills them with real
      // costing-sheet data (asset counts, pricing, callout rates, etc.).
      case 'feature-image': {
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 pointer-events-none" style={{ borderRadius: 6 }}>
            {component.uploadedImage ? (
              <img src={component.uploadedImage} alt="Feature" className="max-w-full max-h-full" style={{ objectFit: component.objectFit || 'cover', width: '100%', height: '100%' }} />
            ) : (
              <div className="text-center text-gray-400">
                <div className="text-2xl mb-1">🌅</div>
                <span className="text-xs">Feature Image (upload or auto from site)</span>
              </div>
            )}
          </div>
        );
      }
      case 'ppm-asset-table': {
        // PPM asset summary — pulls auto_doors, manual_doors, gates, barriers,
        // shutters, total_assets from the costing sheet.
        const assets = [
          { label: 'Automatic Doors', var: 'auto_doors', sample: '4' },
          { label: 'Manual Doors', var: 'manual_doors', sample: '2' },
          { label: 'Gates', var: 'gates', sample: '1' },
          { label: 'Barriers', var: 'barriers', sample: '0' },
          { label: 'Shutters', var: 'shutters', sample: '1' },
        ];
        return (
          <div className="w-full h-full overflow-hidden">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr style={{ backgroundColor: component.headerBg || '#082547', color: component.headerColor || '#ffffff' }}>
                  <th className="border px-2 py-1 text-left font-bold">Asset Type</th>
                  <th className="border px-2 py-1 text-right font-bold">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border px-2 py-1">{a.label}</td>
                    <td className="border px-2 py-1 text-right">{a.sample}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: component.totalBg || '#f0fdf4', fontWeight: 'bold' }}>
                  <td className="border px-2 py-1">Total Assets</td>
                  <td className="border px-2 py-1 text-right">8</td>
                </tr>
              </tbody>
            </table>
            <div className="text-center text-[9px] text-gray-500 mt-1 italic">Preview — data from costing sheet</div>
          </div>
        );
      }
      case 'ppm-pricing-table': {
        // PPM pricing breakdown — per_visit, annual_value, contract_value,
        // discount_percent, subtotal, vat_amount, total.
        const rows = [
          { label: 'Per Visit', var: 'per_visit', sample: '£317.50' },
          { label: 'Annual Value', var: 'annual_value', sample: '£635.00' },
          { label: `Contract Value (${component.contractLength || '3'} yrs)`, var: 'contract_value', sample: '£1,905.00' },
          { label: 'Discount Applied', var: 'discount_percent', sample: '5%' },
          { label: 'Subtotal', var: 'subtotal', sample: '£1,905.00' },
          { label: 'VAT (20%)', var: 'vat_amount', sample: '£381.00' },
        ];
        return (
          <div className="w-full h-full overflow-hidden">
            <table className="w-full text-[10px] border-collapse">
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border px-2 py-1">{r.label}</td>
                    <td className="border px-2 py-1 text-right font-semibold">{r.sample}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: component.totalBg || '#082547', color: '#ffffff', fontWeight: 'bold' }}>
                  <td className="border px-2 py-1">Total</td>
                  <td className="border px-2 py-1 text-right">£2,286.00</td>
                </tr>
              </tbody>
            </table>
            <div className="text-center text-[9px] text-gray-500 mt-1 italic">Preview — data from costing sheet</div>
          </div>
        );
      }
      case 'ppm-callout-rates': {
        // Out-of-hours callout rates — standard, response, emergency, out-of-hours.
        const rates = [
          { label: 'Standard Callout', var: 'callout_standard', sample: '£220' },
          { label: 'Response Callout', var: 'callout_response', sample: '£80' },
          { label: 'Emergency Callout', var: 'callout_emergency', sample: '£315' },
          { label: 'Out of Hours', var: 'callout_out_of_hours', sample: '£395' },
        ];
        return (
          <div className="w-full h-full overflow-hidden">
            <div className="text-[11px] font-bold mb-1" style={{ color: component.headerColor || '#082547' }}>
              {component.title || 'Out-of-Hours Callout Rates'}
            </div>
            <table className="w-full text-[10px] border-collapse">
              <tbody>
                {rates.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border px-2 py-1">{r.label}</td>
                    <td className="border px-2 py-1 text-right font-semibold">{r.sample}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center text-[9px] text-gray-500 mt-1 italic">Preview — data from settings</div>
          </div>
        );
      }
      case 'ppm-scope': {
        // Service scope block — editable heading + body text, defaulting to PPM scope copy.
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full overflow-y-auto pointer-events-none" style={{ padding: '8px 12px' }}>
            <div className="text-[12px] font-bold mb-1" style={{ color: c.headingColor || '#082547' }}>
              {c.heading || 'Service Scope'}
            </div>
            <div className="text-[10px] whitespace-pre-wrap" style={{ color: c.bodyColor || '#333' }}>
              {c.body || 'Our PPM service includes:\n• Bi-annual inspection and service of all automated equipment\n• Safety device testing\n• Lubrication and adjustment\n• Compliance certification'}
            </div>
          </div>
        );
      }
      case 'ppm-terms': {
        // Terms & conditions — numbered list, editable.
        const terms = component.termsList || [
          'Quotation valid for 30 days',
          'Prices exclude VAT',
          'Payment terms: Net 30 days',
          'Contract minimum term 12 months',
          'Cancellation requires 30 days written notice',
        ];
        return (
          <div className="w-full h-full overflow-y-auto pointer-events-none" style={{ padding: '8px 12px' }}>
            <div className="text-[12px] font-bold mb-1" style={{ color: component.headingColor || '#082547' }}>
              {component.heading || 'Terms & Conditions'}
            </div>
            <ol className="text-[10px] text-gray-700 list-decimal pl-4 space-y-0.5">
              {terms.map((t, i) => <li key={i}>{t}</li>)}
            </ol>
          </div>
        );
      }
      case 'ppm-acceptance': {
        // Acceptance form — customer signs to accept the quote.
        return (
          <div className="w-full h-full overflow-hidden pointer-events-none" style={{ padding: '12px' }}>
            <div className="text-[12px] font-bold mb-2" style={{ color: component.headingColor || '#082547' }}>
              Acceptance of Quote
            </div>
            <div className="text-[10px] text-gray-700 mb-3">
              I accept the above quotation and agree to the terms and conditions set out in this document.
            </div>
            <div className="flex justify-between gap-4 text-[10px]">
              <div className="flex-1">
                <div className="text-gray-500 mb-1">Name:</div>
                <div style={{ borderBottom: '1px solid #333', height: '20px' }}></div>
              </div>
              <div className="flex-1">
                <div className="text-gray-500 mb-1">Signature:</div>
                <div style={{ borderBottom: '1px solid #333', height: '20px' }}></div>
              </div>
            </div>
            <div className="flex justify-between gap-4 text-[10px] mt-3">
              <div className="flex-1">
                <div className="text-gray-500 mb-1">Date:</div>
                <div style={{ borderBottom: '1px solid #333', height: '20px' }}></div>
              </div>
              <div className="flex-1">
                <div className="text-gray-500 mb-1">Position:</div>
                <div style={{ borderBottom: '1px solid #333', height: '20px' }}></div>
              </div>
            </div>
          </div>
        );
      }
      case 'ppm-renewal-banner': {
        // Renewal banner — shown when quote variant is 'renewal'.
        const c = component.contentObj || {};
        return (
          <div className="w-full h-full flex items-center justify-center pointer-events-none" style={{
            backgroundColor: c.bgColor || '#EC7626',
            color: c.textColor || '#ffffff',
            borderRadius: 6,
            padding: '8px 16px',
          }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
              {c.text || '🔁 PPM Contract Renewal'}
            </span>
          </div>
        );
      }
      case 'ppm-valid-until': {
        // Validity block — "Valid until {{valid_until}}"
        return (
          <div className="w-full h-full flex items-center pointer-events-none" style={{ padding: '6px 12px' }}>
            <span style={{ fontSize: '11px', color: component.color || '#666' }}>
              {component.label || 'This quotation is valid until:'} <strong>{getSampleData('valid_until')}</strong>
            </span>
          </div>
        );
      }
      case 'page-break': {
        return (
          <div className="w-full h-full flex items-center justify-center bg-blue-50 border-2 border-dashed border-blue-300 pointer-events-none">
            <span className="text-blue-500 text-xs font-medium">⎯⎯ Page Break ⎯⎯</span>
          </div>
        );
      }
      case 'page-number': {
        return (
          <div className="w-full h-full flex items-center justify-center pointer-events-none" style={{ color: component.color || '#999', fontSize: '9px', textAlign: 'center' }}>
            {'Page {{ page_number }} of {{ total_pages }}'}
          </div>
        );
      }
      case 'signature': {
        return (
          <div className="w-full h-full flex items-end pointer-events-none" style={{ padding: '4px 8px' }}>
            <div className="w-full">
              <div style={{ borderBottom: '1px solid #333', height: '30px', marginBottom: '4px' }}></div>
              <div style={{ fontSize: '9px', color: '#666' }}>{component.label || 'Service Manager'}</div>
            </div>
          </div>
        );
      }
      case 'rectangle': {
        return (
          <div className="w-full h-full pointer-events-none" style={{
            border: `${component.borderWidth || 1}px solid ${component.borderColor || '#082547'}`,
            backgroundColor: component.fillColor || 'transparent',
            borderRadius: component.borderRadius || 0,
          }}></div>
        );
      }
      default:
        return <div>Unknown</div>;
    }
  };

  const handles = [
    { pos: 'nw', cursor: 'nw-resize', class: '-top-1 -left-1' },
    { pos: 'n', cursor: 'n-resize', class: '-top-1 left-1/2 -translate-x-1/2' },
    { pos: 'ne', cursor: 'ne-resize', class: '-top-1 -right-1' },
    { pos: 'e', cursor: 'e-resize', class: 'top-1/2 -translate-y-1/2 -right-1' },
    { pos: 'se', cursor: 'se-resize', class: '-bottom-1 -right-1' },
    { pos: 's', cursor: 's-resize', class: '-bottom-1 left-1/2 -translate-x-1/2' },
    { pos: 'sw', cursor: 'sw-resize', class: '-bottom-1 -left-1' },
    { pos: 'w', cursor: 'w-resize', class: 'top-1/2 -translate-y-1/2 -left-1' },
  ];

  // Background color based on component type or custom color
  const getBackgroundColor = () => {
    // If backgrounds are hidden, return transparent
    if (!showBackgrounds) return 'transparent';

    // Use custom color if set
    if (component.backgroundColor) return component.backgroundColor;

    // Default colors by type
    switch (component.type) {
      case 'heading': return '#eef2ff'; // Light indigo
      case 'table': return '#f0fdf4'; // Light green
      case 'line': return '#9ca3af'; // Gray
      case 'spacer': return '#f9fafb'; // Very light gray
      // Email mode blocks
      case 'header': return component.contentObj?.bgColor || '#1a365d';
      case 'footer': return component.contentObj?.bgColor || '#1a365d';
      case 'button': return 'transparent';
      case 'social': return '#f9fafb';
      case '2col': return '#ffffff';
      // Mobile mode blocks
      case 'input': return '#ffffff';
      case 'textarea': return '#ffffff';
      case 'select': return '#ffffff';
      case 'checkbox': return '#ffffff';
      // PPM components
      case 'ppm-asset-table': return '#f8fafc';
      case 'ppm-pricing-table': return '#f8fafc';
      case 'ppm-callout-rates': return '#f8fafc';
      case 'ppm-scope': return '#f8fafc';
      case 'ppm-terms': return '#f8fafc';
      case 'ppm-acceptance': return '#f8fafc';
      case 'ppm-renewal-banner': return 'transparent';
      case 'ppm-valid-until': return '#f8fafc';
      case 'feature-image': return '#f3f4f6';
      case 'page-break': return '#eff6ff';
      case 'page-number': return '#ffffff';
      case 'signature': return '#ffffff';
      case 'rectangle': return 'transparent';
      default: return 'white';
    }
  };

  return (
    <div
      ref={drag}
      style={{
        position: 'absolute',
        left: (component.x || 0) * zoom,
        top: (component.y || 0) * zoom,
        width: (component.width || 200) * zoom,
        height: (component.height || 50) * zoom,
        cursor: component.locked ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab'),
        opacity: 1,
        zIndex: isSelected ? 100 : (component.zIndex || 1),
        backgroundColor: getBackgroundColor()
      }}
      className={`${
        isSelected
          ? 'ring-2 ring-blue-500 shadow-lg'
          : isMultiSelected
            ? 'ring-2 ring-blue-300 shadow-md'
            : 'hover:ring-2 hover:ring-gray-300'
      } ${isDragging ? 'ring-4 ring-blue-600 shadow-2xl' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        // Pass ctrl state from click event (not from prop)
        onSelect(component.id, e.ctrlKey || e.metaKey);
      }}
    >
      {renderContent()}

      {isSelected && !component.locked && handles.map(h => (
        <div
          key={h.pos}
          onMouseDown={(e) => startResize(e, h.pos)}
          className={`absolute w-2 h-2 bg-blue-500 border border-white rounded-full ${h.class} z-50 hover:bg-blue-600`}
          style={{ cursor: h.cursor, pointerEvents: 'auto' }}
        />
      ))}

      {isSelected && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpdate(component.id, { locked: !component.locked });
            }}
            className="absolute -top-6 left-0 w-5 h-5 bg-gray-700 text-white rounded text-xs hover:bg-gray-800 flex items-center justify-center"
          >
            {component.locked ? '🔒' : '🔓'}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              const text = component.content || component.variable || 'component';
              navigator.clipboard.writeText(JSON.stringify(component));
              alert(`Copied ${text.substring(0, 20)}`);
            }}
            className="absolute -top-6 left-6 w-5 h-5 bg-gray-700 text-white rounded text-xs hover:bg-gray-800"
            title="Copy"
          >
            📋
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Delete?')) onDelete(component.id);
            }}
            className="absolute -top-6 right-0 w-5 h-5 bg-red-500 text-white rounded text-xs hover:bg-red-600"
          >
            ×
          </button>
        </>
      )}

      {component.locked && <div className="absolute top-0 left-0 w-4 h-4 bg-gray-700 text-white rounded-br text-xs flex items-center justify-center">🔒</div>}
      {component.groupId && <div className="absolute top-0 right-0 w-4 h-4 bg-purple-600 text-white rounded-bl text-xs flex items-center justify-center" title="Grouped">🔗</div>}
    </div>
  );
}

function Canvas({ components, onAddComponent, onUpdateComponent, onBatchUpdateComponents, onDeleteComponent, selectedId, selectedIds, onSelectComponent, zoom, snapToGridEnabled, showGrid, gridSize, canvasSize, snapPreview, setSnapPreview, showBackgrounds, isEmailMode }) {
  const canvasRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [guides, setGuides] = useState([]);
  const guidesUpdateTimerRef = useRef(null);
  const lastGuidesRef = useRef([]);
  const [shiftPressed, setShiftPressed] = useState(false);
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const dragSnapDisabled = useRef(false); // Tracks if snap was disabled at drag START

  const pageWidth = PAPER_SIZES[canvasSize]?.width || PAGE_WIDTH;
  const pageHeight = PAPER_SIZES[canvasSize]?.height || PAGE_HEIGHT;

  // Multi-page support: count page-break components to determine total pages.
  // The canvas grows to stack pages vertically with a small visual gap between
  // them. Components below a page-break render on the next page.
  const pageBreaks = components.filter(c => c.type === 'page-break');
  const pageCount = pageBreaks.length + 1;
  const PAGE_GAP = 20; // visual gap between pages in the builder (px, pre-zoom)
  const totalCanvasHeight = pageCount * pageHeight + (pageCount - 1) * PAGE_GAP;

  // Track Shift and Ctrl key states
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') setShiftPressed(true);
      if (e.key === 'Control' || e.key === 'Meta') setCtrlPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') setShiftPressed(false);
      if (e.key === 'Control' || e.key === 'Meta') setCtrlPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const [, drop] = useDrop(() => ({
    accept: ['NEW_COMPONENT', 'EXISTING_COMPONENT'],
    hover: (item, monitor) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      // On first hover of this drag, capture the Shift state
      if (!item.dragStarted) {
        item.dragStarted = true;
        dragSnapDisabled.current = shiftPressed;
      }

      let clientOffset;
      if (item.componentType) {
        clientOffset = monitor.getClientOffset();
      } else {
        const sourceOffset = monitor.getSourceClientOffset();
        clientOffset = sourceOffset || monitor.getClientOffset();
      }

      if (!clientOffset) return;

      let x = (clientOffset.x - rect.left) / zoom;
      let y = (clientOffset.y - rect.top) / zoom;

      // Get component size for preview
      let width = 200;
      let height = 40;
      if (item.id && components) {
        const comp = components.find(c => c.id === item.id);
        if (comp) {
          width = comp.width;
          height = comp.height;
        }
      }

      // Apply snap if enabled AND Shift was NOT pressed when drag started
      const shouldSnap = snapToGridEnabled && !dragSnapDisabled.current;
      if (shouldSnap) {
        // First try edge-to-edge alignment snap (to other components)
        const edgeSnap = applyAlignmentSnap(x, y, width, height, components, item.id);
        const didEdgeSnap = (edgeSnap.x !== x || edgeSnap.y !== y);

        if (didEdgeSnap) {
          // Edge snap took precedence
          x = edgeSnap.x;
          y = edgeSnap.y;

          // Only show guides when actually snapped (not just close)
          // This prevents flickering as guides only appear when locked to alignment
          const currentComponent = { id: item.id || 'temp', x, y, width, height };
          const newGuides = findAlignmentGuides(currentComponent, components);

          // Filter to only show guides we're actually snapped to (within 0.5px tolerance)
          const activeGuides = newGuides.filter(g => {
            if (g.type === 'vertical') {
              const compRight = x + (width || 200);
              return Math.abs(x - g.pos) < 0.5 || Math.abs(compRight - g.pos) < 0.5;
            } else {
              const compBottom = y + (height || 50);
              return Math.abs(y - g.pos) < 0.5 || Math.abs(compBottom - g.pos) < 0.5;
            }
          });

          // Debounce guide updates - only update after position stabilizes for 30ms
          if (guidesUpdateTimerRef.current) {
            clearTimeout(guidesUpdateTimerRef.current);
          }

          const guidesKey = JSON.stringify(activeGuides);
          const lastKey = JSON.stringify(lastGuidesRef.current);

          if (guidesKey !== lastKey) {
            lastGuidesRef.current = activeGuides;
            guidesUpdateTimerRef.current = setTimeout(() => {
              setGuides(activeGuides);
            }, 30);
          }
        } else {
          // Fall back to grid snap
          const gridSnap = snapToGrid(x, y, gridSize);
          x = gridSnap.x;
          y = gridSnap.y;
          setGuides([]);
        }
      } else {
        setGuides([]);
      }

      setSnapPreview({
        x,
        y,
        width,
        height
      });
    },
    drop: (item, monitor) => {
      setSnapPreview(null);
      if (guidesUpdateTimerRef.current) {
        clearTimeout(guidesUpdateTimerRef.current);
      }
      setGuides([]);
      const rect = canvasRef.current.getBoundingClientRect();

      // For new components, use mouse position
      // For existing components, use component's top-left corner
      let clientOffset;
      if (item.componentType) {
        // New component - use mouse position
        clientOffset = monitor.getClientOffset();
      } else {
        // Existing component - get its actual position (top-left corner)
        const sourceOffset = monitor.getSourceClientOffset();
        clientOffset = sourceOffset || monitor.getClientOffset();
      }

      // Calculate position relative to canvas (accounting for zoom)
      let x = (clientOffset.x - rect.left) / zoom;
      let y = (clientOffset.y - rect.top) / zoom;

      // Get component size
      let width = 200;
      let height = 50;
      if (item.id && components) {
        const comp = components.find(c => c.id === item.id);
        if (comp) {
          width = comp.width;
          height = comp.height;
        }
      }

      // Apply snapping if enabled AND Shift was NOT pressed when drag started
      const shouldSnap = snapToGridEnabled && !dragSnapDisabled.current;
      if (shouldSnap) {
        // First try edge-to-edge alignment snap
        const edgeSnap = applyAlignmentSnap(x, y, width, height, components, item.id);
        const didEdgeSnap = (edgeSnap.x !== x || edgeSnap.y !== y);

        if (didEdgeSnap) {
          x = edgeSnap.x;
          y = edgeSnap.y;
        } else {
          // Fall back to grid snap
          const gridSnap = snapToGrid(x, y, gridSize);
          x = gridSnap.x;
          y = gridSnap.y;
        }
      }

      // Reset drag state and clear guides
      dragSnapDisabled.current = false;
      setGuides([]);

      // Clamp to canvas bounds and ensure integer pixels
      x = Math.round(Math.max(0, Math.min(pageWidth - 50, x)));
      y = Math.round(Math.max(0, Math.min(totalCanvasHeight - 50, y)));

      if (item.componentType) {
        onAddComponent(item.componentType, x, y);
      } else if (item.id) {
        const comp = components.find(c => c.id === item.id);
        if (comp && !comp.locked) {
          // Check if this is a multi-select drag
          if (item.isMultiSelect && item.selectedIds && item.selectedIds.length > 1) {
            // Calculate offset from original position
            const dx = x - item.startX;
            const dy = y - item.startY;

            // Move all selected components by the same offset
            const updates = item.selectedIds.map(id => {
              const c = components.find(comp => comp.id === id);
              if (c && !c.locked) {
                return {
                  id: c.id,
                  changes: {
                    x: Math.round(Math.max(0, Math.min(pageWidth - (c.width || 200), c.x + dx))),
                    y: Math.round(Math.max(0, Math.min(totalCanvasHeight - (c.height || 50), c.y + dy)))
                  }
                };
              }
              return null;
            }).filter(Boolean);

            // Batch update all components at once
            if (updates.length > 0 && onBatchUpdateComponents) {
              onBatchUpdateComponents(updates);
            }
          } else if (comp.groupId) {
            // Grouped component - move entire group
            const dx = x - item.startX;
            const dy = y - item.startY;

            const groupedComponents = components.filter(c => c.groupId === comp.groupId);
            const updates = groupedComponents.map(c => ({
              id: c.id,
              changes: {
                x: Math.round(Math.max(0, Math.min(pageWidth - (c.width || 200), c.x + dx))),
                y: Math.round(Math.max(0, Math.min(totalCanvasHeight - (c.height || 50), c.y + dy)))
              }
            }));

            if (updates.length > 0 && onBatchUpdateComponents) {
              onBatchUpdateComponents(updates);
            }
          } else {
            // Single component drag
            onUpdateComponent(item.id, { x, y });
          }
        }
      }
      setSnapPreview(null);
    }
  }), [onAddComponent, onUpdateComponent, components, zoom, snapToGridEnabled, showGrid, gridSize, canvasSize, pageWidth, pageHeight, setSnapPreview, shiftPressed]);

  // Ruler interval based on zoom (always readable, always grid-aligned)
  const rulerInterval = gridSize >= 20 ? gridSize : (gridSize * Math.ceil(20 / gridSize));
  const horizontalMarkers = Math.ceil(pageWidth / rulerInterval);
  const verticalMarkers = Math.ceil(pageHeight / rulerInterval);

  return (
    <div className="relative">
      {/* Rulers */}
      <div className="absolute top-0 left-8 right-0 h-6 bg-gray-100 border-b flex z-20">
        {Array.from({ length: horizontalMarkers + 1 }).map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: (i * rulerInterval * zoom),
              height: '100%'
            }}
          >
            <div className="absolute bottom-0 left-0 w-px h-3 bg-gray-600" />
            <span className="absolute bottom-0 left-1 text-[10px] font-medium text-gray-700">{i * rulerInterval}</span>
          </div>
        ))}
      </div>

      <div className="absolute top-6 left-0 bottom-0 w-8 bg-gray-100 border-r flex z-20">
        {Array.from({ length: verticalMarkers + 1 }).map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: (i * rulerInterval * zoom),
              width: '100%'
            }}
          >
            <div className="absolute top-0 right-0 h-px w-3 bg-gray-600" />
            <span className="absolute top-1 left-1 text-[10px] font-medium text-gray-700">{i * rulerInterval}</span>
          </div>
        ))}
      </div>

      <div className="overflow-auto" style={{ width: '100%', height: '100%', paddingLeft: '2rem', paddingTop: '1.5rem' }}>
        <div
          ref={(node) => {
            canvasRef.current = node;
            drop(node);
          }}
          className="relative bg-white border border-gray-200"
          style={{
            width: pageWidth * zoom,
            height: totalCanvasHeight * zoom,
            backgroundImage: showGrid
              ? `linear-gradient(#d1d5db 1px, transparent 1px), linear-gradient(90deg, #d1d5db 1px, transparent 1px)`
              : 'none',
            backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`
          }}
          onClick={() => onSelectComponent(null)}
        >
          {/* Page dividers — visual gaps between pages for multi-page templates */}
          {pageBreaks.length > 0 && Array.from({ length: pageCount - 1 }).map((_, i) => (
            <div
              key={`page-divider-${i}`}
              className="absolute pointer-events-none"
              style={{
                left: 0,
                top: ((i + 1) * pageHeight + i * PAGE_GAP) * zoom,
                width: pageWidth * zoom,
                height: PAGE_GAP * zoom,
                background: '#e5e7eb',
                borderBottom: '1px solid #d1d5db',
                borderTop: '1px solid #d1d5db',
                zIndex: 500,
              }}
            >
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] text-gray-400 font-medium">
                — Page {i + 2} —
              </div>
            </div>
          ))}
          {/* Alignment guides (edge-to-edge snapping) */}
          {guides.map((g, i) => (
            g.type === 'vertical' ? (
              <div
                key={`guide-${i}`}
                className="absolute pointer-events-none bg-blue-500 transition-all duration-75"
                style={{
                  left: g.pos * zoom,
                  top: 0,
                  width: '2px',
                  height: pageHeight * zoom,
                  zIndex: 999
                }}
              />
            ) : (
              <div
                key={`guide-${i}`}
                className="absolute pointer-events-none bg-blue-500 transition-all duration-75"
                style={{
                  left: 0,
                  top: g.pos * zoom,
                  width: pageWidth * zoom,
                  height: '2px',
                  zIndex: 999
                }}
              />
            )
          ))}

          {/* Snap preview ghost */}
          {snapPreview && (
            <div
              className="absolute pointer-events-none border-2 border-dashed border-blue-400 bg-blue-50 bg-opacity-30 rounded"
              style={{
                left: snapPreview.x * zoom,
                top: snapPreview.y * zoom,
                width: snapPreview.width * zoom,
                height: snapPreview.height * zoom,
                zIndex: 998
              }}
            >
              <div className="absolute top-1 left-1 text-[9px] font-mono text-blue-600 bg-white px-1 rounded">
                {Math.round(snapPreview.x)}, {Math.round(snapPreview.y)}
              </div>
            </div>
          )}

          {/* Email zone labels — show which zone each component belongs to */}
          {isEmailMode && components.some(c => c.zone) && (() => {
            const zoneGroups = {};
            components.forEach(c => {
              if (c.zone) {
                if (!zoneGroups[c.zone]) zoneGroups[c.zone] = { minY: Infinity, maxY: 0, zone: c.zone };
                zoneGroups[c.zone].minY = Math.min(zoneGroups[c.zone].minY, c.y);
                zoneGroups[c.zone].maxY = Math.max(zoneGroups[c.zone].maxY, (c.y + (c.height || 40)));
              }
            });
            return Object.entries(zoneGroups).map(([zoneName, bounds]) => {
              const zoneConfig = EMAIL_ZONES[zoneName];
              if (!zoneConfig) return null;
              const isFixed = zoneConfig.fixed;
              const isRequired = zoneConfig.required;
              const label = zoneConfig.label;
              const badgeColor = isFixed ? 'bg-gray-700' : isRequired ? 'bg-orange-500' : 'bg-blue-500';
              return (
                <div
                  key={`zone-label-${zoneName}`}
                  className={`absolute pointer-events-none ${badgeColor} text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-br-sm rounded-tl-sm shadow-sm`}
                  style={{
                    left: 0,
                    top: bounds.minY * zoom,
                    zIndex: 900,
                    opacity: 0.85,
                  }}
                >
                  {label}{isRequired ? ' ●' : ''}{isFixed ? ' 🔒' : ''}
                </div>
              );
            });
          })()}

          {components.filter(c => c && c.id).map(c => (
            <CanvasComponent
              key={c.id}
              component={c}
              isSelected={selectedId === c.id}
              isMultiSelected={selectedIds && selectedIds.includes(c.id)}
              selectedIds={selectedIds}
              onSelect={onSelectComponent}
              onUpdate={onUpdateComponent}
              onDelete={onDeleteComponent}
              zoom={zoom}
              showBackgrounds={showBackgrounds}
              ctrlPressed={ctrlPressed}
            />
          ))}

          {!components.length && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400">
              <div className="text-center">
                <p className="text-lg font-medium mb-2">Drag components here</p>
                <p className="text-sm">Start building from the palette</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Alignment toolbar component
function AlignmentToolbar({ components, selectedIds = [], onUpdate, onBatchUpdate }) {
  // Handle alignment operations for selected components
  const handleAlign = (type) => {
    if (!selectedIds || selectedIds.length === 0) return;

    const selectedComps = components.filter(c => selectedIds.includes(c.id));
    if (selectedComps.length === 0) return;

    switch (type) {
      case 'left': {
        const leftmost = Math.min(...selectedComps.map(c => c.x));
        const updates = selectedComps.map(c => ({
          id: c.id,
          changes: { x: leftmost }
        }));
        if (onBatchUpdate) {
          onBatchUpdate(updates);
        } else {
          updates.forEach(u => onUpdate(u.id, u.changes));
        }
        break;
      }
      case 'right': {
        const rightmost = Math.max(...selectedComps.map(c => c.x + (c.width || 200)));
        const updates = selectedComps.map(c => ({ id: c.id, changes: { x: rightmost - (c.width || 200) } }));
        onBatchUpdate ? onBatchUpdate(updates) : updates.forEach(u => onUpdate(u.id, u.changes));
        break;
      }
      case 'top': {
        const topmost = Math.min(...selectedComps.map(c => c.y));
        const updates = selectedComps.map(c => ({ id: c.id, changes: { y: topmost } }));
        onBatchUpdate ? onBatchUpdate(updates) : updates.forEach(u => onUpdate(u.id, u.changes));
        break;
      }
      case 'bottom': {
        const bottommost = Math.max(...selectedComps.map(c => c.y + (c.height || 50)));
        const updates = selectedComps.map(c => ({ id: c.id, changes: { y: bottommost - (c.height || 50) } }));
        onBatchUpdate ? onBatchUpdate(updates) : updates.forEach(u => onUpdate(u.id, u.changes));
        break;
      }
      case 'center-h': {
        const centerY = selectedComps.reduce((sum, c) => sum + c.y + ((c.height || 50) / 2), 0) / selectedComps.length;
        const updates = selectedComps.map(c => ({ id: c.id, changes: { y: centerY - ((c.height || 50) / 2) } }));
        onBatchUpdate ? onBatchUpdate(updates) : updates.forEach(u => onUpdate(u.id, u.changes));
        break;
      }
      case 'center-v': {
        const centerX = selectedComps.reduce((sum, c) => sum + c.x + ((c.width || 200) / 2), 0) / selectedComps.length;
        const updates = selectedComps.map(c => ({ id: c.id, changes: { x: centerX - ((c.width || 200) / 2) } }));
        onBatchUpdate ? onBatchUpdate(updates) : updates.forEach(u => onUpdate(u.id, u.changes));
        break;
      }
      case 'distribute-h': {
        if (selectedComps.length < 3) return;
        const sorted = [...selectedComps].sort((a, b) => a.x - b.x);
        const leftmost = sorted[0].x;
        const rightmost = sorted[sorted.length - 1].x + (sorted[sorted.length - 1].width || 200);
        const totalWidth = sorted.reduce((sum, c) => sum + (c.width || 200), 0);
        const gap = (rightmost - leftmost - totalWidth) / (sorted.length - 1);
        let currentX = leftmost;
        sorted.forEach(c => {
          onUpdate(c.id, { x: currentX });
          currentX += (c.width || 200) + gap;
        });
        break;
      }
      case 'distribute-v': {
        if (selectedComps.length < 3) return;
        const sorted = [...selectedComps].sort((a, b) => a.y - b.y);
        const topmost = sorted[0].y;
        const bottommost = sorted[sorted.length - 1].y + (sorted[sorted.length - 1].height || 50);
        const totalHeight = sorted.reduce((sum, c) => sum + (c.height || 50), 0);
        const gap = (bottommost - topmost - totalHeight) / (sorted.length - 1);
        let currentY = topmost;
        sorted.forEach(c => {
          onUpdate(c.id, { y: currentY });
          currentY += (c.height || 50) + gap;
        });
        break;
      }
    }
  };

  const selectedCount = selectedIds ? selectedIds.length : 0;
  const disabled = selectedCount === 0;
  const distributeDisabled = selectedCount < 3;

  return (
    <div className="flex items-center gap-1 p-2 bg-gray-50 border-b">
      <span className="text-xs text-gray-600 mr-2 font-medium">Align:</span>

      <button
        onClick={() => handleAlign('left')}
        disabled={disabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition flex items-center gap-1"
        title="Align Left Edges"
      >
        <span className="text-base">⫷</span>
        <span>Left</span>
      </button>

      <button
        onClick={() => handleAlign('center-v')}
        disabled={disabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition"
        title="Align Center Vertically"
      >
        <span className="text-base">║</span>
      </button>

      <button
        onClick={() => handleAlign('right')}
        disabled={disabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition flex items-center gap-1"
        title="Align Right Edges"
      >
        <span>Right</span>
        <span className="text-base">⫸</span>
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <button
        onClick={() => handleAlign('top')}
        disabled={disabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition"
        title="Align Top Edges"
      >
        <span className="text-base">⫶</span>
      </button>

      <button
        onClick={() => handleAlign('center-h')}
        disabled={disabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition"
        title="Align Center Horizontally"
      >
        <span className="text-base">═</span>
      </button>

      <button
        onClick={() => handleAlign('bottom')}
        disabled={disabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition"
        title="Align Bottom Edges"
      >
        <span className="text-base">⫯</span>
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <button
        onClick={() => handleAlign('distribute-h')}
        disabled={distributeDisabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition"
        title="Distribute Horizontally (3+ items)"
      >
        ⬌ Dist H
      </button>

      <button
        onClick={() => handleAlign('distribute-v')}
        disabled={distributeDisabled}
        className="px-2 py-1 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded text-xs disabled:opacity-30 disabled:cursor-not-allowed transition"
        title="Distribute Vertically (3+ items)"
      >
        ⬍ Dist V
      </button>

      {selectedCount > 0 && (
        <span className="ml-auto text-xs text-gray-500">
          {selectedCount} selected
        </span>
      )}
    </div>
  );
}

function LayersPanel({ components, selectedId, selectedIds, onSelect, onUpdate, onDelete }) {
  const sorted = [...components].sort((a, b) => (b.zIndex || 1) - (a.zIndex || 1));

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3 border-b pb-2">
        <h3 className="font-semibold text-gray-700">Layers</h3>
        <span className="text-xs text-gray-500">{components.length} items</span>
      </div>
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {sorted.map(c => {
          const isSelected = selectedId === c.id;
          const isMultiSelected = selectedIds && selectedIds.includes(c.id) && !isSelected;

          return (
            <div
              key={c.id}
              onClick={(e) => onSelect(c.id, e.ctrlKey || e.metaKey)}
              className={`p-1.5 rounded cursor-pointer text-xs flex items-center justify-between ${
                isSelected
                  ? 'bg-blue-100 border border-blue-300'
                  : isMultiSelected
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
            <div className="flex items-center space-x-1.5 flex-1 truncate min-w-0">
              <span className="text-sm">{COMPONENT_TYPES.find(t => t.id === c.type)?.icon}</span>
              <span className="truncate">
                {c.content?.substring(0, 12) || c.variable || COMPONENT_TYPES.find(t => t.id === c.type)?.label}
              </span>
              {c.locked && <span className="text-xs">🔒</span>}
            </div>
            <div className="flex gap-0.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate(c.id, { zIndex: (c.zIndex || 1) + 1 });
                }}
                className="w-4 h-4 bg-gray-200 hover:bg-gray-300 rounded text-[10px] flex items-center justify-center"
                title="Bring forward"
              >↑</button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate(c.id, { zIndex: Math.max(1, (c.zIndex || 1) - 1) });
                }}
                className="w-4 h-4 bg-gray-200 hover:bg-gray-300 rounded text-[10px] flex items-center justify-center"
                title="Send backward"
              >↓</button>
            </div>
          </div>
          );
        })}
        {!components.length && <p className="text-xs text-gray-400 text-center py-4">No layers yet</p>}
      </div>
    </div>
  );
}

function SettingsPanel({ snapToGridEnabled, setSnapToGridEnabled, showGrid, setShowGrid, gridSize, setGridSize, zoom, setZoom, canvasSize, setCanvasSize, showBackgrounds, setShowBackgrounds, handleUndo, handleRedo, history, historyIndex, selectedIds, components, onBatchUpdate, onGroupComponents }) {
  const currentSize = PAPER_SIZES[canvasSize] || { width: 600, height: 800, label: 'Email' };

  const groupComponents = () => {
    if (!selectedIds || selectedIds.length < 2) return;
    onGroupComponents(selectedIds);
  };

  const alignComponents = (type) => {
    if (!selectedIds || selectedIds.length < 2) return;

    const selected = components.filter(c => selectedIds.includes(c.id));
    const updates = [];

    if (type === 'left') {
      const minX = Math.min(...selected.map(c => c.x));
      selected.forEach(c => updates.push({ id: c.id, changes: { x: minX } }));
    } else if (type === 'right') {
      const maxRight = Math.max(...selected.map(c => c.x + (c.width || 200)));
      selected.forEach(c => updates.push({ id: c.id, changes: { x: maxRight - (c.width || 200) } }));
    } else if (type === 'center-h') {
      const avgX = selected.reduce((sum, c) => sum + c.x + (c.width || 200) / 2, 0) / selected.length;
      selected.forEach(c => updates.push({ id: c.id, changes: { x: avgX - (c.width || 200) / 2 } }));
    } else if (type === 'top') {
      const minY = Math.min(...selected.map(c => c.y));
      selected.forEach(c => updates.push({ id: c.id, changes: { y: minY } }));
    } else if (type === 'bottom') {
      const maxBottom = Math.max(...selected.map(c => c.y + (c.height || 50)));
      selected.forEach(c => updates.push({ id: c.id, changes: { y: maxBottom - (c.height || 50) } }));
    } else if (type === 'middle') {
      const avgY = selected.reduce((sum, c) => sum + c.y + (c.height || 50) / 2, 0) / selected.length;
      selected.forEach(c => updates.push({ id: c.id, changes: { y: avgY - (c.height || 50) / 2 } }));
    }

    if (updates.length) onBatchUpdate(updates);
  };

  const distributeComponents = (direction) => {
    if (!selectedIds || selectedIds.length < 3) return;

    const selected = components.filter(c => selectedIds.includes(c.id));
    const updates = [];

    if (direction === 'horizontal') {
      const sorted = [...selected].sort((a, b) => a.x - b.x);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const totalWidth = (last.x + (last.width || 200)) - first.x;
      const gap = (totalWidth - sorted.reduce((sum, c) => sum + (c.width || 200), 0)) / (sorted.length - 1);

      let currentX = first.x;
      sorted.forEach(c => {
        if (c.id !== first.id) {
          updates.push({ id: c.id, changes: { x: currentX } });
        }
        currentX += (c.width || 200) + gap;
      });
    } else if (direction === 'vertical') {
      const sorted = [...selected].sort((a, b) => a.y - b.y);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const totalHeight = (last.y + (last.height || 50)) - first.y;
      const gap = (totalHeight - sorted.reduce((sum, c) => sum + (c.height || 50), 0)) / (sorted.length - 1);

      let currentY = first.y;
      sorted.forEach(c => {
        if (c.id !== first.id) {
          updates.push({ id: c.id, changes: { y: currentY } });
        }
        currentY += (c.height || 50) + gap;
      });
    }

    if (updates.length) onBatchUpdate(updates);
  };

  return (
    <div className="p-4 space-y-6">
      <h3 className="font-semibold text-gray-700 border-b pb-2">Canvas Settings</h3>

      {/* Canvas Size */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Paper Size</h4>
        <select
          value={canvasSize}
          onChange={(e) => setCanvasSize(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-2"
        >
          {Object.entries(PAPER_SIZES).map(([key, size]) => (
            <option key={key} value={key}>
              {size.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500">
          Canvas: {currentSize.width} × {currentSize.height} pixels
        </p>
      </div>

      {/* Undo/Redo */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">History</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className={`flex-1 px-4 py-2 rounded font-medium text-sm transition ${
              historyIndex <= 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-300 hover:bg-gray-400 text-black'
            }`}
            title="Undo (Ctrl+Z)"
          >
            ↶ Undo
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className={`flex-1 px-4 py-2 rounded font-medium text-sm transition ${
              historyIndex >= history.length - 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-300 hover:bg-gray-400 text-black'
            }`}
            title="Redo (Ctrl+Y)"
          >
            ↷ Redo
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Step {historyIndex + 1} of {history.length}
          {history.length >= 50 && ' (max 50)'}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          <kbd className="px-1 bg-gray-100 border rounded text-xs">Ctrl+Z</kbd> Undo ·
          <kbd className="px-1 bg-gray-100 border rounded text-xs ml-1">Ctrl+Y</kbd> Redo
        </p>
      </div>

      {/* Multi-Select Alignment Tools */}
      {selectedIds && selectedIds.length > 1 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Align Multiple ({selectedIds.length} selected)
          </h4>

          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-2">Horizontal Alignment</label>
              <div className="grid grid-cols-3 gap-1">
                <button
                  onClick={() => alignComponents('left')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ⬅ Left
                </button>
                <button
                  onClick={() => alignComponents('center-h')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ↔ Center
                </button>
                <button
                  onClick={() => alignComponents('right')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ➡ Right
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-2">Vertical Alignment</label>
              <div className="grid grid-cols-3 gap-1">
                <button
                  onClick={() => alignComponents('top')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ⬆ Top
                </button>
                <button
                  onClick={() => alignComponents('middle')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ↕ Middle
                </button>
                <button
                  onClick={() => alignComponents('bottom')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ⬇ Bottom
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-2">Distribute</label>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => distributeComponents('horizontal')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ↔ Horizontal
                </button>
                <button
                  onClick={() => distributeComponents('vertical')}
                  className="px-2 py-1.5 text-xs bg-gray-300 hover:bg-gray-400 rounded font-medium"
                >
                  ↕ Vertical
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-2">Grouping</label>
              <button
                onClick={groupComponents}
                className="w-full px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
              >
                🔗 Group Components
              </button>
              <p className="text-xs text-gray-500 mt-1">Move grouped items together</p>
            </div>
          </div>
        </div>
      )}

      {/* Grid Settings */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Grid & Snapping</h4>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Show Grid</label>
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`px-4 py-2 rounded font-medium text-sm transition ${
                showGrid
                  ? 'bg-blue-500 hover:bg-blue-400 text-white'
                  : 'bg-gray-300 hover:bg-gray-400 text-black'
              }`}
            >
              {showGrid ? '👁️ ON' : '👁️ OFF'}
            </button>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Snap to Grid</label>
              <button
                onClick={() => setSnapToGridEnabled(!snapToGridEnabled)}
                className={`px-4 py-2 rounded font-medium text-sm transition ${
                  snapToGridEnabled
                    ? 'bg-green-500 hover:bg-green-400 text-white'
                    : 'bg-gray-300 hover:bg-gray-400 text-black'
                }`}
              >
                {snapToGridEnabled ? '🧲 ON' : '🧲 OFF'}
              </button>
            </div>
            {snapToGridEnabled && (
              <p className="text-xs text-gray-500 mt-1">
                💡 Hold <kbd className="px-1 bg-gray-100 border rounded">Shift</kbd> while dragging to disable snapping temporarily
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Show Backgrounds</label>
            <button
              onClick={() => setShowBackgrounds(!showBackgrounds)}
              className={`px-4 py-2 rounded font-medium text-sm transition ${
                showBackgrounds
                  ? 'bg-blue-500 hover:bg-blue-400 text-white'
                  : 'bg-gray-300 hover:bg-gray-400 text-black'
              }`}
            >
              {showBackgrounds ? '🎨 ON' : '🎨 OFF'}
            </button>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-2">Grid Size</label>
            <select
              value={gridSize}
              onChange={(e) => setGridSize(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="1">1 pixel (ultra-fine)</option>
              <option value="5">5 pixels</option>
              <option value="10">10 pixels (default)</option>
              <option value="20">20 pixels</option>
              <option value="25">25 pixels</option>
              <option value="50">50 pixels</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {showGrid && 'Grid lines: '}
              {showGrid && !snapToGridEnabled && 'visible (no snapping)'}
              {showGrid && snapToGridEnabled && `visible, snapping to ${gridSize}px`}
              {!showGrid && snapToGridEnabled && `hidden, snapping to ${gridSize}px`}
              {!showGrid && !snapToGridEnabled && 'hidden (free positioning)'}
            </p>
          </div>
        </div>
      </div>

      {/* Zoom Settings */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Zoom Level</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-gray-700">{Math.round(zoom * 100)}%</span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setZoom(Math.max(0.25, zoom - 0.1))}
                className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded font-bold"
              >
                −
              </button>
              <button
                onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded font-bold"
              >
                +
              </button>
            </div>
          </div>

          <input
            type="range"
            min="25"
            max="200"
            value={zoom * 100}
            onChange={(e) => setZoom(parseInt(e.target.value) / 100)}
            className="w-full"
          />

          <div className="flex justify-between text-xs text-gray-500">
            <span>25%</span>
            <span>200%</span>
          </div>

          <div className="grid grid-cols-4 gap-1 mt-3">
            {[0.5, 0.8, 1, 1.5].map(z => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={`px-2 py-1 text-xs rounded ${
                  Math.abs(zoom - z) < 0.01
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {Math.round(z * 100)}%
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Keyboard Shortcuts</h4>
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>Delete component</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded border">Delete</kbd>
          </div>
          <div className="flex justify-between">
            <span>Nudge 1px</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded border">Arrow keys</kbd>
          </div>
          <div className="flex justify-between">
            <span>Nudge 10px</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded border">Shift + Arrows</kbd>
          </div>
          <div className="flex justify-between">
            <span>Paste</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded border">Ctrl/Cmd + V</kbd>
          </div>
        </div>
      </div>
    </div>
  );
}

function PropertiesPanel({ component, onUpdate, availableVariables, setShowTableEditor }) {
  const [showVarPicker, setShowVarPicker] = useState(false);
  const [propsLibImages, setPropsLibImages] = useState([]);
  const [propsLibLoading, setPropsLibLoading] = useState(false);

  if (!component) {
    return <div className="p-4 text-center text-gray-500"><p className="text-sm">Select a component to edit</p></div>;
  }

  return (
    <div className="p-4 space-y-4">
      <h3 className="font-semibold text-gray-700 border-b pb-2">Properties</h3>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Position & Size</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'X', key: 'x', value: component.x },
            { label: 'Y', key: 'y', value: component.y },
            { label: 'W', key: 'width', value: component.width || 200 },
            { label: 'H', key: 'height', value: component.height || 50 }
          ].map(field => (
            <div key={field.key}>
              <label className="text-gray-500 text-xs">{field.label}:</label>
              <input
                type="number"
                value={Math.round(field.value)}
                onChange={(e) => onUpdate(component.id, { [field.key]: parseInt(e.target.value) || 0 })}
                className="w-full px-2 py-1 border rounded text-xs mt-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Content / Variable Selection - moved up for easy access */}
      {(component.type === 'text' || component.type === 'heading') && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Content</label>
          <textarea
            value={component.content || ''}
            onChange={(e) => onUpdate(component.id, { content: e.target.value })}
            className="w-full px-2 py-1 border rounded text-sm"
            rows="3"
          />
          <button
            onClick={() => setShowVarPicker(true)}
            className="mt-2 w-full bg-blue-100 text-blue-700 px-3 py-1 rounded text-xs hover:bg-blue-200"
          >
            Insert Variable
          </button>
        </div>
      )}

      {component.type === 'variable' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Variable</label>
          <button
            onClick={() => setShowVarPicker(true)}
            className="w-full bg-blue-100 text-blue-700 px-3 py-2 rounded text-sm hover:bg-blue-200"
          >
            {component.variable ? `{{${component.variable}}}` : 'Select Variable'}
          </button>
        </div>
      )}

      {/* Background Color */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Background Color</label>
        <div className="flex items-center space-x-2 mb-2">
          <div
            className="w-10 h-10 rounded border-2 border-gray-300"
            style={{ backgroundColor: component.backgroundColor || '#ffffff' }}
            title="Current color"
          />
          <input
            type="text"
            value={component.backgroundColor || '#ffffff'}
            onChange={(e) => onUpdate(component.id, { backgroundColor: e.target.value })}
            className="flex-1 px-2 py-1.5 border rounded text-xs font-mono"
            placeholder="#ffffff"
          />
          <button
            onClick={() => onUpdate(component.id, { backgroundColor: '' })}
            className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded transition"
            title="Clear background"
          >
            Clear
          </button>
        </div>
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-2">Color Palette (light → dark):</p>
          <div className="flex gap-1">
            {/* Each color family in a vertical column */}
            {[
              { name: 'Gray', colors: ['#ffffff', '#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#374151', '#000000'] },
              { name: 'Blue', colors: ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1e40af'] },
              { name: 'Green', colors: ['#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d'] },
              { name: 'Yellow', colors: ['#fefce8', '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309'] },
              { name: 'Orange', colors: ['#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c'] },
              { name: 'Red', colors: ['#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c'] },
              { name: 'Purple', colors: ['#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7e22ce'] },
              { name: 'Teal', colors: ['#f0fdfa', '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6', '#0d9488', '#0f766e'] }
            ].map(family => (
              <div key={family.name} className="flex flex-col gap-1">
                <div className="text-center text-[9px] text-gray-400 mb-0.5">{family.name}</div>
                {family.colors.map(color => {
                  const isActive = (component.backgroundColor || '').toLowerCase() === color.toLowerCase();
                  return (
                    <button
                      key={color}
                      onClick={() => onUpdate(component.id, { backgroundColor: color })}
                      className={`w-7 h-7 rounded transition ${
                        isActive
                          ? 'ring-2 ring-blue-600 border-2 border-white shadow-md'
                          : 'border hover:ring-2 hover:ring-blue-400'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Text Color (for text/heading/variable components) */}
      {(component.type === 'text' || component.type === 'heading' || component.type === 'variable') && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
          <div className="flex items-center space-x-2 mb-2">
            <div
              className="w-10 h-10 rounded border-2 border-gray-300"
              style={{ backgroundColor: component.color || '#000000' }}
              title="Current text color"
            />
            <input
              type="text"
              value={component.color || '#000000'}
              onChange={(e) => onUpdate(component.id, { color: e.target.value })}
              className="flex-1 px-2 py-1.5 border rounded text-xs font-mono"
              placeholder="#000000"
            />
            <button
              onClick={() => onUpdate(component.id, { color: '' })}
              className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded transition"
              title="Reset to black"
            >
              Reset
            </button>
          </div>
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1">Quick Colors:</p>
            <div className="flex gap-1">
              {[
                { name: 'Black/Gray', colors: ['#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#ffffff'] },
                { name: 'Blue', colors: ['#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff'] },
                { name: 'Red', colors: ['#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2', '#fef2f2'] },
                { name: 'Green', colors: ['#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac', '#bbf7d0', '#dcfce7', '#f0fdf4'] }
              ].map(family => (
                <div key={family.name} className="flex flex-col gap-1">
                  <div className="text-center text-[9px] text-gray-400 mb-0.5">{family.name}</div>
                  {family.colors.map(color => {
                    const isActive = (component.color || '').toLowerCase() === color.toLowerCase();
                    return (
                      <button
                        key={color}
                        onClick={() => onUpdate(component.id, { color: color })}
                        className={`w-7 h-7 rounded transition ${
                          isActive
                            ? 'ring-2 ring-blue-600 border-2 border-white shadow-md'
                            : 'border hover:ring-2 hover:ring-blue-400'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {component.type === 'table' && (
        <div className="space-y-3">
          <button
            onClick={() => setShowTableEditor(true)}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition text-sm"
          >
            ✏️ Edit Table
          </button>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Table Preset</label>
            <select
              value={component.tablePreset || 'custom'}
              onChange={(e) => {
                const preset = e.target.value;
                let updates = { tablePreset: preset };

                if (preset === 'parts-only') {
                  updates = {
                    ...updates,
                    tableRows: 4,
                    tableCols: 5,
                    tableData: [
                      ['Part No', 'Description', 'Qty', 'Price', 'Total'],
                      ['', '', '', '', ''],
                      ['', '', '', '', ''],
                      ['', '', '', '', '']
                    ],
                    tableHeaderBgColor: '#2563eb',
                    tableHeaderTextColor: '#ffffff'
                  };
                } else if (preset === 'parts-labor') {
                  updates = {
                    ...updates,
                    tableRows: 5,
                    tableCols: 4,
                    tableData: [
                      ['Description', 'Qty/Hours', 'Rate', 'Total'],
                      ['', '', '', ''],
                      ['', '', '', ''],
                      ['', '', '', ''],
                      ['', '', '', '']
                    ],
                    tableHeaderBgColor: '#2563eb',
                    tableHeaderTextColor: '#ffffff'
                  };
                } else if (preset === 'simple-list') {
                  updates = {
                    ...updates,
                    tableRows: 4,
                    tableCols: 2,
                    tableData: [
                      ['Item', 'Details'],
                      ['', ''],
                      ['', ''],
                      ['', '']
                    ],
                    tableHeaderBgColor: '#6b7280',
                    tableHeaderTextColor: '#ffffff'
                  };
                }

                onUpdate(component.id, updates);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="custom">Custom</option>
              <option value="parts-only">📦 Parts Only (Part No, Desc, Qty, Price, Total)</option>
              <option value="parts-labor">🔧 Parts + Labor (Desc, Qty/Hours, Rate, Total)</option>
              <option value="simple-list">📋 Simple List (Item, Details)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {component.tablePreset === 'custom' && 'Fully customizable table'}
              {component.tablePreset === 'parts-only' && 'Pre-configured for product invoices'}
              {component.tablePreset === 'parts-labor' && 'For invoices with parts and labor'}
              {component.tablePreset === 'simple-list' && 'Basic two-column layout'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Rows</label>
              <input
                type="number"
                min="1"
                max="20"
                value={component.tableRows || 3}
                onChange={(e) => {
                  const newRows = parseInt(e.target.value) || 3;
                  const currentData = component.tableData || [['Cell']];
                  const newData = Array.from({ length: newRows }, (_, rowIdx) =>
                    Array.from({ length: component.tableCols || 3 }, (_, colIdx) =>
                      currentData[rowIdx]?.[colIdx] || `R${rowIdx + 1}C${colIdx + 1}`
                    )
                  );
                  onUpdate(component.id, { tableRows: newRows, tableData: newData });
                }}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Columns</label>
              <input
                type="number"
                min="1"
                max="10"
                value={component.tableCols || 3}
                onChange={(e) => {
                  const newCols = parseInt(e.target.value) || 3;
                  const currentData = component.tableData || [['Cell']];
                  const newData = currentData.map((row, rowIdx) =>
                    Array.from({ length: newCols }, (_, colIdx) =>
                      row[colIdx] || `R${rowIdx + 1}C${colIdx + 1}`
                    )
                  );
                  onUpdate(component.id, { tableCols: newCols, tableData: newData });
                }}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Edit Cells</label>
            <div className="max-h-64 overflow-auto border rounded p-2 bg-gray-50">
              {(component.tableData || [['Cell']]).map((row, rowIdx) => (
                <div key={rowIdx} className="mb-2">
                  <div className="text-xs text-gray-500 mb-1">
                    {rowIdx === 0 ? '📌 Header Row' : `Row ${rowIdx + 1}`}
                  </div>
                  {row.map((cell, colIdx) => (
                    <input
                      key={colIdx}
                      type="text"
                      value={cell}
                      onChange={(e) => {
                        const newData = [...(component.tableData || [])];
                        newData[rowIdx][colIdx] = e.target.value;
                        onUpdate(component.id, { tableData: newData });
                      }}
                      placeholder={`R${rowIdx + 1}C${colIdx + 1}`}
                      className="w-full px-2 py-1 border rounded text-xs mb-1"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Header Row Styling</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Header Background</label>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-gray-300"
                    style={{ backgroundColor: component.tableHeaderBgColor || '#f3f4f6' }}
                  />
                  <input
                    type="text"
                    value={component.tableHeaderBgColor || '#f3f4f6'}
                    onChange={(e) => onUpdate(component.id, { tableHeaderBgColor: e.target.value })}
                    className="flex-1 px-2 py-1.5 border rounded text-xs font-mono"
                    placeholder="#f3f4f6"
                  />
                  <button
                    onClick={() => onUpdate(component.id, { tableHeaderBgColor: '#f3f4f6' })}
                    className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Reset
                  </button>
                </div>
                <div className="mt-2 flex gap-1">
                  {['#f3f4f6', '#dbeafe', '#dcfce7', '#fef3c7', '#fee2e2', '#f3e8ff', '#e0e7ff', '#fef08a'].map(color => (
                    <button
                      key={color}
                      onClick={() => onUpdate(component.id, { tableHeaderBgColor: color })}
                      className="w-8 h-8 rounded border hover:ring-2 hover:ring-blue-400"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Header Text Color</label>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-gray-300"
                    style={{ backgroundColor: component.tableHeaderTextColor || '#000000' }}
                  />
                  <input
                    type="text"
                    value={component.tableHeaderTextColor || '#000000'}
                    onChange={(e) => onUpdate(component.id, { tableHeaderTextColor: e.target.value })}
                    className="flex-1 px-2 py-1.5 border rounded text-xs font-mono"
                    placeholder="#000000"
                  />
                  <button
                    onClick={() => onUpdate(component.id, { tableHeaderTextColor: '#000000' })}
                    className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Reset
                  </button>
                </div>
                <div className="mt-2 flex gap-1">
                  {['#000000', '#374151', '#6b7280', '#ffffff', '#1e40af', '#dc2626', '#16a34a', '#d97706'].map(color => (
                    <button
                      key={color}
                      onClick={() => onUpdate(component.id, { tableHeaderTextColor: color })}
                      className="w-8 h-8 rounded border hover:ring-2 hover:ring-blue-400"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {component.type === 'data-table' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Table Preset</label>
            <select
              value={component.dataTablePreset || 'parts-only'}
              onChange={(e) => {
                const preset = e.target.value;
                onUpdate(component.id, { dataTablePreset: preset });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="parts-only">📦 Parts Only</option>
              <option value="parts-labor">🔧 Parts + Labor</option>
              <option value="simple-list">📋 Simple List</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {component.dataTablePreset === 'parts-only' && 'Part No | Description | Qty | Price | Total'}
              {component.dataTablePreset === 'parts-labor' && 'Description | Qty/Hours | Rate | Total'}
              {component.dataTablePreset === 'simple-list' && 'Item | Details'}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Data Source</label>
            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <span className="font-mono text-blue-700">{'{{items_table}}'}</span>
              <p className="text-xs text-gray-600 mt-1">Pulls from invoice/order line items</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Header Styling</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Header Background</label>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-gray-300"
                    style={{ backgroundColor: component.tableHeaderBgColor || '#2563eb' }}
                  />
                  <input
                    type="text"
                    value={component.tableHeaderBgColor || '#2563eb'}
                    onChange={(e) => onUpdate(component.id, { tableHeaderBgColor: e.target.value })}
                    className="flex-1 px-2 py-1.5 border rounded text-xs font-mono"
                    placeholder="#2563eb"
                  />
                  <button
                    onClick={() => onUpdate(component.id, { tableHeaderBgColor: '#2563eb' })}
                    className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Reset
                  </button>
                </div>
                <div className="mt-2 flex gap-1">
                  {['#2563eb', '#059669', '#dc2626', '#7c3aed', '#ea580c', '#0891b2', '#4b5563', '#000000'].map(color => (
                    <button
                      key={color}
                      onClick={() => onUpdate(component.id, { tableHeaderBgColor: color })}
                      className="w-8 h-8 rounded border hover:ring-2 hover:ring-blue-400"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Header Text Color</label>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-10 h-10 rounded border-2 border-gray-300"
                    style={{ backgroundColor: component.tableHeaderTextColor || '#ffffff' }}
                  />
                  <input
                    type="text"
                    value={component.tableHeaderTextColor || '#ffffff'}
                    onChange={(e) => onUpdate(component.id, { tableHeaderTextColor: e.target.value })}
                    className="flex-1 px-2 py-1.5 border rounded text-xs font-mono"
                    placeholder="#ffffff"
                  />
                  <button
                    onClick={() => onUpdate(component.id, { tableHeaderTextColor: '#ffffff' })}
                    className="px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Reset
                  </button>
                </div>
                <div className="mt-2 flex gap-1">
                  {['#ffffff', '#f3f4f6', '#e5e7eb', '#000000', '#1f2937', '#374151', '#6b7280', '#9ca3af'].map(color => (
                    <button
                      key={color}
                      onClick={() => onUpdate(component.id, { tableHeaderTextColor: color })}
                      className="w-8 h-8 rounded border hover:ring-2 hover:ring-blue-400"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {component.type === 'image' && (
        <div className="space-y-3">
          {/* Position */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-gray-500">X</label>
                <input type="number" value={component.x || 0} onChange={e => onUpdate(component.id, { x: parseInt(e.target.value) || 0 })} className="w-full px-2 py-1 border rounded text-sm" /></div>
              <div><label className="text-[10px] text-gray-500">Y</label>
                <input type="number" value={component.y || 0} onChange={e => onUpdate(component.id, { y: parseInt(e.target.value) || 0 })} className="w-full px-2 py-1 border rounded text-sm" /></div>
            </div>
          </div>
          {/* Size */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Size</label>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-gray-500">Width</label>
                <input type="number" value={component.width || 200} onChange={e => onUpdate(component.id, { width: parseInt(e.target.value) || 200 })} className="w-full px-2 py-1 border rounded text-sm" min="10" /></div>
              <div><label className="text-[10px] text-gray-500">Height</label>
                <input type="number" value={component.height || 150} onChange={e => onUpdate(component.id, { height: parseInt(e.target.value) || 150 })} className="w-full px-2 py-1 border rounded text-sm" min="10" /></div>
            </div>
          </div>
          {/* Image Source */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Image Source</label>
            <select
              value={component.imageSource || 'library'}
              onChange={(e) => {
                const src = e.target.value;
                if (src === 'library') {
                  onUpdate(component.id, { imageSource: 'library' });
                  setPropsLibLoading(true);
                  (async () => {
                    try {
                      const res = await apiFetch(`${API_URL}/api/marketing/images`);
                      if (res.ok) { const data = await res.json(); setPropsLibImages(data.images || []); }
                    } catch (err) { console.error(err); }
                    setPropsLibLoading(false);
                  })();
                } else {
                  onUpdate(component.id, { imageSource: src });
                }
              }}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="library">Image Library</option>
              <option value="variable">Dynamic Variable</option>
              <option value="upload">Upload New to Library</option>
            </select>

            {/* Library picker — shows images from Render disk */}
            {component.imageSource === 'library' && (
              <div className="mt-1">
                {component.src && (
                  <div className="border rounded p-1 bg-white mb-2">
                    <img src={component.src} alt="Selected" className="w-full object-contain" style={{ maxHeight: '60px' }} />
                    <button onClick={() => onUpdate(component.id, { src: '', variable: '' })} className="mt-1 w-full bg-red-100 text-red-700 px-2 py-0.5 rounded text-[11px] hover:bg-red-200">Remove</button>
                  </div>
                )}
                {!component.src && (
                  <>
                    <button
                      onClick={() => {
                        setPropsLibLoading(true);
                        (async () => {
                          try {
                            const res = await apiFetch(`${API_URL}/api/marketing/images`);
                            if (res.ok) { const data = await res.json(); setPropsLibImages(data.images || []); }
                          } catch (err) { console.error(err); }
                          setPropsLibLoading(false);
                        })();
                      }}
                      className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1.5 rounded text-xs font-medium border border-blue-200 mb-1"
                    >📁 Browse Library</button>
                    {propsLibLoading && <p className="text-xs text-gray-400 text-center">Loading...</p>}
                    {propsLibImages.length > 0 && (
                      <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto border rounded p-1.5 bg-gray-50">
                        {propsLibImages.map(img => (
                          <button
                            key={img.filename}
                            onClick={() => onUpdate(component.id, { src: img.url, variable: '' })}
                            className="group relative rounded border bg-white hover:border-blue-400 hover:ring-1 hover:ring-blue-300 overflow-hidden"
                            title={img.label}
                          >
                            <img src={img.url} alt={img.label} className="w-full aspect-square object-contain p-0.5" />
                            <span className="block text-[9px] text-gray-500 truncate px-0.5 pb-0.5 group-hover:text-blue-700">{img.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {propsLibImages.length === 0 && !propsLibLoading && (
                      <p className="text-xs text-gray-400 italic">No images in library yet. Use Upload New or 🖼️ Image tab.</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Variable picker */}
            {(component.imageSource === 'variable' || (!component.imageSource && false)) && (
              <select
                value={component.variable || ''}
                onChange={(e) => onUpdate(component.id, { variable: e.target.value })}
                className="w-full px-2 py-1 border rounded text-sm mt-1"
              >
                <option value="">Select variable...</option>
                <option value="company_logo">Company Logo</option>
              </select>
            )}

            {/* Upload new to library */}
            {component.imageSource === 'upload' && (
              <label className="block mt-1">
                <p className="text-[10px] text-gray-500 mb-1">Uploads to server library, then auto-selects</p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('image', file);
                      formData.append('label', file.name.replace(/\.[^.]+$/, ''));
                      const token = localStorage.getItem('doorbase_token');
                      const res = await fetch(`${API_URL}/api/marketing/images`, {
                        method: 'POST',
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                        body: formData,
                      });
                      if (!res.ok) { const err = await res.json(); alert(err.error || 'Upload failed'); return; }
                      const data = await res.json();
                      onUpdate(component.id, { src: data.url, imageSource: 'library' });
                    } catch (err) { console.error('Upload error:', err); alert(err.message || 'Upload failed'); }
                    e.target.value = '';
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </label>
            )}
          </div>
          {/* Fit + Align → Image tab for full control */}
          <p className="text-xs text-gray-500 italic">Fit & Align controls → 🖼️ Image tab</p>
        </div>
      )}

      {/* ─── Email block property editors ─── */}
      {component.type === 'header' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Company Text</label>
              <input type="text" value={co.companyText || co.text || ''} onChange={e => updateCO('companyText', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Logo</label>
              <input type="text" value={co.logoUrl || ''} onChange={e => updateCO('logoUrl', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" placeholder="Paste URL or Use from 🖼️ Image tab" />
              {co.logoUrl && (
                <div className="flex items-center gap-2 mt-1 p-1 bg-white rounded border">
                  <img src={co.logoUrl} alt="Logo" className="h-6 object-contain" />
                  <button onClick={() => { updateCO('logoUrl', ''); updateCO('logoLabel', ''); }} className="text-xs text-red-500 hover:text-red-700">✕</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">BG Color</label>
                <input type="color" value={co.bgColor || '#1a365d'} onChange={e => updateCO('bgColor', e.target.value)} className="w-full h-8 rounded" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
                <input type="color" value={co.textColor || '#ffffff'} onChange={e => updateCO('textColor', e.target.value)} className="w-full h-8 rounded" /></div>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Font Size</label>
              <input type="number" value={co.fontSize || 22} onChange={e => updateCO('fontSize', parseInt(e.target.value) || 22)} className="w-full px-2 py-1 border rounded text-sm" min="12" max="48" /></div>
          </div>
        );
      })()}

      {component.type === 'button' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
              <input type="text" value={co.text || ''} onChange={e => updateCO('text', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
              <input type="text" value={co.url || ''} onChange={e => updateCO('url', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">BG Color</label>
                <input type="color" value={co.bgColor || '#e53e3e'} onChange={e => updateCO('bgColor', e.target.value)} className="w-full h-8 rounded" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
                <input type="color" value={co.textColor || '#ffffff'} onChange={e => updateCO('textColor', e.target.value)} className="w-full h-8 rounded" /></div>
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Border Radius</label>
              <input type="number" value={co.borderRadius ?? 4} onChange={e => updateCO('borderRadius', parseInt(e.target.value) || 0)} className="w-full px-2 py-1 border rounded text-sm" min="0" max="30" /></div>
          </div>
        );
      })()}

      {component.type === 'divider' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
              <input type="color" value={co.color || '#dddddd'} onChange={e => updateCO('color', e.target.value)} className="w-full h-8 rounded" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Thickness (px)</label>
              <input type="number" value={co.thickness || 1} onChange={e => updateCO('thickness', parseInt(e.target.value) || 1)} className="w-full px-2 py-1 border rounded text-sm" min="1" max="10" /></div>
          </div>
        );
      })()}

      {component.type === 'social' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        const links = co.links || [];
        const updateLink = (idx, key, val) => {
          const newLinks = [...links];
          newLinks[idx] = { ...newLinks[idx], [key]: val };
          updateCO('links', newLinks);
        };
        const addLink = () => updateCO('links', [...links, { platform: 'website', url: '' }]);
        const removeLink = (idx) => updateCO('links', links.filter((_, i) => i !== idx));
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Link Color</label>
              <input type="color" value={co.color || '#1a365d'} onChange={e => updateCO('color', e.target.value)} className="w-full h-8 rounded" /></div>
            {links.map((link, i) => (
              <div key={i} className="border rounded p-2 bg-gray-50 space-y-1">
                <div className="flex gap-1">
                  <select value={link.platform} onChange={e => updateLink(i, 'platform', e.target.value)} className="flex-1 px-1 py-0.5 border rounded text-xs">
                    <option value="website">Website</option><option value="facebook">Facebook</option><option value="linkedin">LinkedIn</option><option value="twitter">Twitter</option><option value="instagram">Instagram</option><option value="youtube">YouTube</option>
                  </select>
                  <button onClick={() => removeLink(i)} className="text-red-500 text-xs px-1">✕</button>
                </div>
                <input type="text" value={link.url || ''} onChange={e => updateLink(i, 'url', e.target.value)} className="w-full px-1 py-0.5 border rounded text-xs" placeholder="https://..." />
              </div>
            ))}
            <button onClick={addLink} className="w-full px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">+ Add Link</button>
          </div>
        );
      })()}

      {component.type === 'footer' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
              <input type="text" value={co.company || ''} onChange={e => updateCO('company', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input type="text" value={co.phone || ''} onChange={e => updateCO('phone', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input type="text" value={co.email || ''} onChange={e => updateCO('email', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
              <input type="text" value={co.website || ''} onChange={e => updateCO('website', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
              <input type="text" value={co.address || ''} onChange={e => updateCO('address', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">BG Color</label>
                <input type="color" value={co.bgColor || '#1a365d'} onChange={e => updateCO('bgColor', e.target.value)} className="w-full h-8 rounded" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Text Color</label>
                <input type="color" value={co.textColor || '#ffffff'} onChange={e => updateCO('textColor', e.target.value)} className="w-full h-8 rounded" /></div>
            </div>
          </div>
        );
      })()}

      {component.type === '2col' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        const left = co.left || {};
        const right = co.right || {};
        return (
          <div className="space-y-3">
            <div className="border rounded p-2 bg-blue-50 space-y-1">
              <label className="block text-xs font-medium text-blue-700 mb-1">Left Column</label>
              <textarea value={left.text || ''} onChange={e => updateCO('left', { ...left, text: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" rows="2" />
              <input type="color" value={left.color || '#333333'} onChange={e => updateCO('left', { ...left, color: e.target.value })} className="w-full h-6 rounded" />
            </div>
            <div className="border rounded p-2 bg-green-50 space-y-1">
              <label className="block text-xs font-medium text-green-700 mb-1">Right Column</label>
              <textarea value={right.text || ''} onChange={e => updateCO('right', { ...right, text: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" rows="2" />
              <input type="color" value={right.color || '#333333'} onChange={e => updateCO('right', { ...right, color: e.target.value })} className="w-full h-6 rounded" />
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Gap (px)</label>
              <input type="number" value={co.gap || 20} onChange={e => updateCO('gap', parseInt(e.target.value) || 0)} className="w-full px-2 py-1 border rounded text-sm" min="0" max="60" /></div>
          </div>
        );
      })()}

      {/* ─── Mobile block property editors ─── */}
      {component.type === 'input' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input type="text" value={co.label || ''} onChange={e => updateCO('label', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
              <input type="text" value={co.placeholder || ''} onChange={e => updateCO('placeholder', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Input Type</label>
              <select value={co.inputType || 'text'} onChange={e => updateCO('inputType', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                <option value="text">Text</option><option value="email">Email</option><option value="tel">Phone</option><option value="number">Number</option><option value="date">Date</option><option value="url">URL</option>
              </select></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!co.required} onChange={e => updateCO('required', e.target.checked)} className="rounded" />
              <label className="text-xs font-medium text-gray-600">Required</label>
            </div>
          </div>
        );
      })()}

      {component.type === 'textarea' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input type="text" value={co.label || ''} onChange={e => updateCO('label', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
              <input type="text" value={co.placeholder || ''} onChange={e => updateCO('placeholder', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Rows</label>
              <input type="number" value={co.rows || 4} onChange={e => updateCO('rows', parseInt(e.target.value) || 4)} className="w-full px-2 py-1 border rounded text-sm" min="2" max="20" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!co.required} onChange={e => updateCO('required', e.target.checked)} className="rounded" />
              <label className="text-xs font-medium text-gray-600">Required</label>
            </div>
          </div>
        );
      })()}

      {component.type === 'select' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        const options = co.options || ['Option 1', 'Option 2'];
        const updateOption = (idx, val) => {
          const newOpts = [...options];
          newOpts[idx] = val;
          updateCO('options', newOpts);
        };
        const addOption = () => updateCO('options', [...options, `Option ${options.length + 1}`]);
        const removeOption = (idx) => updateCO('options', options.filter((_, i) => i !== idx));
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input type="text" value={co.label || ''} onChange={e => updateCO('label', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Options</label>
              {options.map((opt, i) => (
                <div key={i} className="flex gap-1 mb-1">
                  <input type="text" value={opt} onChange={e => updateOption(i, e.target.value)} className="flex-1 px-2 py-1 border rounded text-xs" />
                  <button onClick={() => removeOption(i)} className="text-red-500 text-xs px-1">✕</button>
                </div>
              ))}
              <button onClick={addOption} className="w-full px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">+ Add Option</button>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!co.required} onChange={e => updateCO('required', e.target.checked)} className="rounded" />
              <label className="text-xs font-medium text-gray-600">Required</label>
            </div>
          </div>
        );
      })()}

      {component.type === 'checkbox' && (() => {
        const co = component.contentObj || {};
        const updateCO = (key, val) => onUpdate(component.id, { contentObj: { ...co, [key]: val } });
        return (
          <div className="space-y-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input type="text" value={co.label || ''} onChange={e => updateCO('label', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!co.checked} onChange={e => updateCO('checked', e.target.checked)} className="rounded" />
              <label className="text-xs font-medium text-gray-600">Default Checked</label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!co.required} onChange={e => updateCO('required', e.target.checked)} className="rounded" />
              <label className="text-xs font-medium text-gray-600">Required</label>
            </div>
          </div>
        );
      })()}

      {['text', 'heading', 'variable'].includes(component.type) && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Font Size</label>
            <input
              type="number"
              value={component.fontSize || 12}
              onChange={(e) => onUpdate(component.id, { fontSize: parseInt(e.target.value) || 12 })}
              className="w-full px-2 py-1 border rounded"
              min="8"
              max="72"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Text Style</label>
            <div className="grid grid-cols-3 gap-1">
              <button
                onClick={() => onUpdate(component.id, { fontWeight: component.fontWeight === 'bold' ? 'normal' : 'bold' })}
                className={`px-2 py-1 text-xs rounded font-bold ${
                  component.fontWeight === 'bold' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                B
              </button>
              <button
                onClick={() => onUpdate(component.id, { fontStyle: component.fontStyle === 'italic' ? 'normal' : 'italic' })}
                className={`px-2 py-1 text-xs rounded italic ${
                  component.fontStyle === 'italic' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                I
              </button>
              <button
                onClick={() => onUpdate(component.id, { textDecoration: component.textDecoration === 'underline' ? 'none' : 'underline' })}
                className={`px-2 py-1 text-xs rounded underline ${
                  component.textDecoration === 'underline' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                U
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Font Family</label>
            <select
              value={component.fontFamily || 'Arial'}
              onChange={(e) => onUpdate(component.id, { fontFamily: e.target.value })}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Trebuchet MS">Trebuchet MS</option>
              <option value="Impact">Impact</option>
              <option value="Comic Sans MS">Comic Sans MS</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Line Height</label>
              <input
                type="number"
                value={component.lineHeight || 1.2}
                onChange={(e) => onUpdate(component.id, { lineHeight: parseFloat(e.target.value) || 1.2 })}
                className="w-full px-2 py-1 border rounded text-sm"
                min="0.8"
                max="3"
                step="0.1"
              />
              <p className="text-xs text-gray-500 mt-1">Spacing between lines</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Letter Spacing</label>
              <input
                type="number"
                value={component.letterSpacing || 0}
                onChange={(e) => onUpdate(component.id, { letterSpacing: parseFloat(e.target.value) || 0 })}
                className="w-full px-2 py-1 border rounded text-sm"
                min="-2"
                max="10"
                step="0.1"
              />
              <p className="text-xs text-gray-500 mt-1">px between letters</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Horizontal Align</label>
            <div className="grid grid-cols-3 gap-1">
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  onClick={() => onUpdate(component.id, { textAlign: align })}
                  className={`px-2 py-1 text-xs rounded ${
                    (component.textAlign || 'left') === align ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Vertical Align</label>
            <div className="grid grid-cols-3 gap-1">
              {[
                { key: 'top', label: 'Top' },
                { key: 'middle', label: 'Middle' },
                { key: 'bottom', label: 'Bottom' }
              ].map(align => (
                <button
                  key={align.key}
                  onClick={() => onUpdate(component.id, { verticalAlign: align.key })}
                  className={`px-2 py-1 text-xs rounded ${
                    (component.verticalAlign || 'top') === align.key ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {align.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Padding (Internal Spacing)</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Top/Bottom</label>
                <input
                  type="number"
                  value={component.paddingY || 4}
                  onChange={(e) => onUpdate(component.id, { paddingY: parseInt(e.target.value) || 0 })}
                  className="w-full px-2 py-1 border rounded text-sm"
                  min="0"
                  max="50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Left/Right</label>
                <input
                  type="number"
                  value={component.paddingX || 4}
                  onChange={(e) => onUpdate(component.id, { paddingX: parseInt(e.target.value) || 0 })}
                  className="w-full px-2 py-1 border rounded text-sm"
                  min="0"
                  max="50"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Space between text and border</p>
          </div>
        </>
      )}

      {showVarPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowVarPicker(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-lg">Insert Variable</h3>
                <button onClick={() => setShowVarPicker(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
              </div>
              <p className="text-sm text-gray-600">
                Select a variable to insert into your template. Variables will be replaced with actual data when generating documents.
              </p>
            </div>

            <div className="p-4 overflow-y-auto max-h-[65vh]">
              {Object.keys(availableVariables).length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-3">⚠️</div>
                  <p className="text-gray-600 font-medium mb-2">No Area Assigned</p>
                  <p className="text-sm text-gray-500">
                    Assign this template to an area (Quotes, Invoices, etc.) in the left sidebar<br />
                    to see available variables for that document type.
                  </p>
                </div>
              ) : (
                Object.entries(availableVariables).map(([cat, vars]) => (
                  <div key={cat} className="mb-5">
                    <div className="flex items-center mb-3">
                      <h4 className="font-semibold text-sm text-gray-700 uppercase tracking-wide">{cat.replace(/_/g, ' ')}</h4>
                      <div className="flex-1 h-px bg-gray-200 ml-3"></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {vars.map(v => (
                        <button
                          key={v}
                          onClick={() => {
                            if (component.type === 'variable') {
                              onUpdate(component.id, { variable: v });
                            } else {
                              onUpdate(component.id, { content: (component.content || '') + `{{${v}}}` });
                            }
                            setShowVarPicker(false);
                          }}
                          className="text-left px-3 py-2.5 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded transition group"
                        >
                          <div className="font-medium text-sm text-gray-800 group-hover:text-blue-700">
                            {v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </div>
                          <div className="text-xs text-gray-500 font-mono mt-0.5">
                            {'{{' + v + '}}'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {Object.keys(availableVariables).length > 0 && (
              <div className="p-4 bg-gray-50 border-t text-xs text-gray-600">
                <strong>Tip:</strong> Variables are specific to the assigned document type.
                Assign multiple areas to see all available variables.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TableEditorModal({ component, onUpdate, onClose }) {
  const [tableData, setTableData] = useState(component.tableData || [['Cell']]);
  const [tableCellStyles, setTableCellStyles] = useState(component.tableCellStyles || {});
  const [selectedCell, setSelectedCell] = useState(null); // { row, col }

  const handleCellChange = (rowIdx, colIdx, value) => {
    const newData = [...tableData];
    newData[rowIdx][colIdx] = value;
    setTableData(newData);
  };

  const handleStyleChange = (style) => {
    if (!selectedCell) return;
    const key = `${selectedCell.row}-${selectedCell.col}`;
    setTableCellStyles(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...style }
    }));
  };

  const getCellStyle = (rowIdx, colIdx) => {
    const key = `${rowIdx}-${colIdx}`;
    return tableCellStyles[key] || {};
  };

  const handleSave = () => {
    onUpdate(component.id, {
      tableData,
      tableCellStyles
    });
    onClose();
  };

  const currentStyle = selectedCell ? getCellStyle(selectedCell.row, selectedCell.col) : {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-lg">
          <h2 className="text-2xl font-bold">✏️ Edit Table</h2>
          <p className="text-sm text-blue-100 mt-1">
            {selectedCell
              ? `Selected: Row ${selectedCell.row + 1}, Column ${selectedCell.col + 1}`
              : 'Click a cell to edit and format'}
          </p>
        </div>

        {/* Toolbar */}
        {selectedCell && (
          <div className="bg-gray-50 border-b px-6 py-3 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Size:</label>
              <input
                type="number"
                min="8"
                max="48"
                value={currentStyle.fontSize || 10}
                onChange={(e) => handleStyleChange({ fontSize: parseInt(e.target.value) })}
                className="w-16 px-2 py-1 border rounded text-sm"
              />
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handleStyleChange({ fontWeight: currentStyle.fontWeight === 'bold' ? 'normal' : 'bold' })}
                className={`px-3 py-1 text-sm rounded font-bold ${
                  currentStyle.fontWeight === 'bold' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                B
              </button>
              <button
                onClick={() => handleStyleChange({ fontStyle: currentStyle.fontStyle === 'italic' ? 'normal' : 'italic' })}
                className={`px-3 py-1 text-sm rounded italic ${
                  currentStyle.fontStyle === 'italic' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                I
              </button>
            </div>

            <div className="flex items-center gap-1">
              <label className="text-xs text-gray-600">Align:</label>
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  onClick={() => handleStyleChange({ textAlign: align })}
                  className={`px-3 py-1 text-xs rounded ${
                    (currentStyle.textAlign || 'left') === align ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  {align[0].toUpperCase()}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Text:</label>
              <input
                type="color"
                value={currentStyle.color || '#000000'}
                onChange={(e) => handleStyleChange({ color: e.target.value })}
                className="w-10 h-8 rounded border cursor-pointer"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Background:</label>
              <input
                type="color"
                value={currentStyle.backgroundColor || '#ffffff'}
                onChange={(e) => handleStyleChange({ backgroundColor: e.target.value })}
                className="w-10 h-8 rounded border cursor-pointer"
              />
              <button
                onClick={() => handleStyleChange({ backgroundColor: '' })}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Table Grid */}
        <div className="flex-1 overflow-auto p-6">
          <table className="w-full border-collapse border-2 border-gray-300">
            <tbody>
              {tableData.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, colIdx) => {
                    const cellStyle = getCellStyle(rowIdx, colIdx);
                    const isSelected = selectedCell?.row === rowIdx && selectedCell?.col === colIdx;
                    const isHeader = rowIdx === 0;

                    return (
                      <td
                        key={colIdx}
                        className={`border-2 p-0 ${
                          isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-300'
                        }`}
                        onClick={() => setSelectedCell({ row: rowIdx, col: colIdx })}
                      >
                        <input
                          type="text"
                          value={cell}
                          onChange={(e) => handleCellChange(rowIdx, colIdx, e.target.value)}
                          className="w-full px-2 py-2 border-none focus:outline-none"
                          style={{
                            fontSize: `${cellStyle.fontSize || 10}px`,
                            fontWeight: cellStyle.fontWeight || (isHeader ? 'bold' : 'normal'),
                            fontStyle: cellStyle.fontStyle || 'normal',
                            textAlign: cellStyle.textAlign || 'left',
                            color: cellStyle.color || '#000000',
                            backgroundColor: cellStyle.backgroundColor || (isHeader ? (component.tableHeaderBgColor || '#f3f4f6') : '#ffffff')
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t px-6 py-4 flex justify-between items-center rounded-b-lg">
          <p className="text-sm text-gray-600">
            {tableData.length} rows × {tableData[0]?.length || 0} columns
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
            >
              💾 Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateBuilder({ mode: modeProp, templateData, onClose, onSave, saveUrl }) {
  const navigate = useNavigate();
  const { id } = useParams();
  // In email mode, templateData from parent determines new vs edit
  const isNew = templateData?.isNew || id === 'new';

  // Mode configuration: form (default), email, mobile, ppm_quote
  // PPM mode activates automatically when the template is assigned to the
  // 'ppm_quote' area (detected via prop, fetched assignments, or the
  // ppm_quote flag on templateData).
  const initialMode = modeProp
    || (templateData?.area === 'ppm_quote' || templateData?.ppm_quote ? PPM_QUOTE_MODE : FORM_MODE);
  const [mode, setMode] = useState(initialMode);
  const isEmailMode = mode.id === 'email';
  const isMobileMode = mode.id === 'mobile';
  const isPpmMode = mode === PPM_QUOTE_MODE;

  // Image manager modal state

  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [propsLibImages, setPropsLibImages] = useState([]);
  const [propsLibLoading, setPropsLibLoading] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState('');
  const fileInputRef = useRef(null);

  // Load images for the sidebar gallery
  const loadLibraryImages = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/marketing/images`);
      if (res.ok) {
        const data = await res.json();
        setLibraryImages(data.images || []);
      }
    } catch (e) { console.error('Failed to load library images:', e); }
    setLibraryLoading(false);
  }, [API_URL]); // { componentId, field }

  // Load images on mount
  useEffect(() => { loadLibraryImages(); }, [loadLibraryImages]);

  // Override component types and paper sizes based on mode
  const COMPONENT_TYPES = mode.components;
  const PAPER_SIZES = mode.canvasSizes;
  const defaultCanvasSize = mode.defaultSize;

  // Parse design_json helper
  const parseDesignJson = (dj) => {
    if (Array.isArray(dj)) return dj;
    if (dj && typeof dj === 'object' && Array.isArray(dj.components)) return dj.components;
    if (typeof dj === 'string') {
      try {
        const parsed = JSON.parse(dj);
        return Array.isArray(parsed) ? parsed : (parsed.components || []);
      } catch (e) { return []; }
    }
    return [];
  };

  // Initialise template from templateData immediately (no async needed for email mode)
  const initialTemplate = (isEmailMode && templateData) ? {
    id: templateData.id || null,
    name: templateData.name || '',
    description: templateData.category || 'commercial',
    layout_json: { components: parseDesignJson(templateData.design_json) },
  } : { name: '', description: '', layout_json: { components: [] } };

  const [companyName, setCompanyName] = useState('');
  const [template, setTemplate] = useState(initialTemplate);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [defaultAreas, setDefaultAreas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]); // For multi-select alignment
  const [loading, setLoading] = useState(isEmailMode ? false : !isNew);
  const [saving, setSaving] = useState(false);

  // Undo/Redo history (max 50 steps)
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoingRedoing = useRef(false); // Prevent history push during undo/redo

  // Copy/paste clipboard
  const copiedComponents = useRef(null);

  // Component groups
  const [groups, setGroups] = useState([]);

  // Load designer preferences from localStorage
  const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem('templateDesigner.zoom')) || 0.8);
  const [showLayers, setShowLayers] = useState(true);
  const [snapToGridEnabled, setSnapToGridEnabled] = useState(() => localStorage.getItem('templateDesigner.snapToGrid') !== 'false');
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showGrid, setShowGrid] = useState(() => localStorage.getItem('templateDesigner.showGrid') !== 'false');
  const [gridSize, setGridSize] = useState(() => parseInt(localStorage.getItem('templateDesigner.gridSize')) || 10);
  const [showBackgrounds, setShowBackgrounds] = useState(() => localStorage.getItem('templateDesigner.showBackgrounds') !== 'false');
  const [rightPanelTab, setRightPanelTab] = useState('properties'); // properties, layers, settings
  const [canvasSize, setCanvasSize] = useState(() => localStorage.getItem('templateDesigner.canvasSize') || defaultCanvasSize);
  const [snapPreview, setSnapPreview] = useState(null); // {x, y, width, height} for ghost preview
  const [showTableEditor, setShowTableEditor] = useState(false);

  // Save preferences to localStorage when they change
  useEffect(() => { localStorage.setItem('templateDesigner.zoom', zoom); }, [zoom]);
  useEffect(() => { localStorage.setItem('templateDesigner.snapToGrid', snapToGridEnabled); }, [snapToGridEnabled]);
  useEffect(() => { localStorage.setItem('templateDesigner.showGrid', showGrid); }, [showGrid]);
  useEffect(() => { localStorage.setItem('templateDesigner.gridSize', gridSize); }, [gridSize]);
  useEffect(() => { localStorage.setItem('templateDesigner.showBackgrounds', showBackgrounds); }, [showBackgrounds]);
  useEffect(() => { localStorage.setItem('templateDesigner.canvasSize', canvasSize); }, [canvasSize]);

  // Push current state to history whenever components change (but not during undo/redo)
  useEffect(() => {
    if (isUndoingRedoing.current) return;
    if (!template.layout_json?.components) return;

    // Don't push initial empty state or duplicate states
    const componentsSnapshot = JSON.parse(JSON.stringify(template.layout_json.components));
    const lastSnapshot = history[historyIndex];

    if (lastSnapshot && JSON.stringify(lastSnapshot) === JSON.stringify(componentsSnapshot)) {
      return; // No change, don't push
    }

    // Truncate history after current index (when making changes after undo)
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(componentsSnapshot);

    // Keep only last 50 steps
    const trimmedHistory = newHistory.slice(-50);

    setHistory(trimmedHistory);
    setHistoryIndex(trimmedHistory.length - 1);
  }, [template.layout_json.components]);

  // Load template data — for email mode, load directly from templateData prop
  useEffect(() => {
    if (isEmailMode && templateData) {
      const dj = templateData.design_json;
      let components = [];
      if (Array.isArray(dj)) {
        components = dj;
      } else if (dj && typeof dj === 'object' && Array.isArray(dj.components)) {
        components = dj.components;
      } else if (typeof dj === 'string') {
        try {
          const parsed = JSON.parse(dj);
          components = Array.isArray(parsed) ? parsed : (parsed.components || []);
        } catch (e) { /* ignore parse error */ }
      }
      // Resolve zone-based components to absolute positions for the canvas
      const hasZoneLayout = components.some(c => c.zone);
      if (hasZoneLayout) {
        components = calculateEmailZonePositions(components);
      }
      setTemplate({
        id: templateData.id || null,
        name: templateData.name || '',
        description: templateData.category || 'commercial',
        layout_json: { components },
      });
      setLoading(false);
    } else if (!isEmailMode && !isNew) {
      fetchTemplate();
    }
    fetchCompanyName();
  }, [templateData, isEmailMode, isNew]);

  const fetchCompanyName = async () => {
    try {
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();
      setCompanyName(data.company_name || 'DoorBase');
    } catch (err) {
      console.error('Error fetching company info:', err);
      setCompanyName('DoorBase');
    }
  };

  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return; // Nothing to undo

    isUndoingRedoing.current = true;
    const prevIndex = historyIndex - 1;
    const prevState = history[prevIndex];

    setTemplate(prev => ({
      ...prev,
      layout_json: {
        ...prev.layout_json,
        components: JSON.parse(JSON.stringify(prevState))
      }
    }));

    setHistoryIndex(prevIndex);

    // Clear flag after state update completes
    setTimeout(() => { isUndoingRedoing.current = false; }, 0);
  }, [historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return; // Nothing to redo

    isUndoingRedoing.current = true;
    const nextIndex = historyIndex + 1;
    const nextState = history[nextIndex];

    setTemplate(prev => ({
      ...prev,
      layout_json: {
        ...prev.layout_json,
        components: JSON.parse(JSON.stringify(nextState))
      }
    }));

    setHistoryIndex(nextIndex);

    // Clear flag after state update completes
    setTimeout(() => { isUndoingRedoing.current = false; }, 0);
  }, [historyIndex, history]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Undo/Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
        return;
      }

      // Ungroup (Ctrl+Shift+G)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'g' && selectedId) {
        e.preventDefault();
        const comp = template.layout_json.components.find(c => c.id === selectedId);
        if (comp && comp.groupId) {
          handleUngroupComponents(comp.groupId);
        }
        return;
      }

      // Copy
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && (selectedId || selectedIds.length > 0)) {
        e.preventDefault();
        const componentsToCopy = selectedIds.length > 1
          ? template.layout_json.components.filter(c => selectedIds.includes(c.id))
          : template.layout_json.components.filter(c => c.id === selectedId);

        if (componentsToCopy.length > 0) {
          copiedComponents.current = JSON.parse(JSON.stringify(componentsToCopy));
        }
        return;
      }

      // Paste
      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        e.preventDefault();
        if (!copiedComponents.current || copiedComponents.current.length === 0) return;

        const newComponents = copiedComponents.current.map((comp, idx) => ({
          ...comp,
          id: `${Date.now()}-${idx}`,
          x: comp.x + 10,
          y: comp.y + 10
        }));

        setTemplate(prev => ({
          ...prev,
          layout_json: {
            ...prev.layout_json,
            components: [...prev.layout_json.components, ...newComponents]
          }
        }));

        // Select the newly pasted components
        if (newComponents.length === 1) {
          setSelectedId(newComponents[0].id);
          setSelectedIds([newComponents[0].id]);
        } else {
          setSelectedIds(newComponents.map(c => c.id));
          setSelectedId(newComponents[newComponents.length - 1].id);
        }
        return;
      }

      if (e.key === 'Delete' && selectedId) {
        const comp = template.layout_json.components.find(c => c.id === selectedId);
        if (comp && !comp.locked && window.confirm('Delete component?')) {
          handleDeleteComponent(selectedId);
        }
      }
      if (selectedId && !e.metaKey && !e.ctrlKey) {
        const comp = template.layout_json.components.find(c => c.id === selectedId);
        if (comp && !comp.locked) {
          const step = e.shiftKey ? 10 : 1;
          let updates = {};
          if (e.key === 'ArrowLeft') { updates.x = Math.max(0, comp.x - step); e.preventDefault(); }
          if (e.key === 'ArrowRight') { updates.x = Math.min(PAGE_WIDTH - (comp.width || 200), comp.x + step); e.preventDefault(); }
          if (e.key === 'ArrowUp') { updates.y = Math.max(0, comp.y - step); e.preventDefault(); }
          if (e.key === 'ArrowDown') { updates.y = Math.min(PAGE_HEIGHT - (comp.height || 50), comp.y + step); e.preventDefault(); }
          if (Object.keys(updates).length) handleUpdateComponent(selectedId, updates);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedIds, template.layout_json.components, handleUndo, handleRedo]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);

      // Email mode: load from marketing API or from templateData prop
      if (isEmailMode) {
        if (templateData && !templateData.isNew) {
          // Load from supplied template data.
          // design_json may be a parsed array of components, or { components: [...] }, or a string
          let components = [];
          const dj = templateData.design_json;
          if (Array.isArray(dj)) {
            components = dj;
          } else if (dj && typeof dj === 'object' && Array.isArray(dj.components)) {
            components = dj.components;
          } else if (typeof dj === 'string') {
            try {
              const parsed = JSON.parse(dj);
              components = Array.isArray(parsed) ? parsed : (parsed.components || []);
            } catch (e) { /* ignore parse error */ }
          }
          // Resolve zone-based components to absolute positions for the canvas
          if (components.some(c => c.zone)) {
            components = calculateEmailZonePositions(components);
          }
          setTemplate({
            id: templateData.id,
            name: templateData.name || '',
            description: templateData.category || '',
            layout_json: { components },
          });
        } else if (templateData?.isNew) {
          // New template — may have starter design_json or start empty
          let components = [];
          const dj = templateData.design_json;
          if (Array.isArray(dj)) {
            components = dj;
          } else if (dj && typeof dj === 'object' && Array.isArray(dj.components)) {
            components = dj.components;
          } else if (typeof dj === 'string') {
            try {
              const parsed = JSON.parse(dj);
              components = Array.isArray(parsed) ? parsed : (parsed.components || []);
            } catch (e) { /* ignore parse error */ }
          }
          // Resolve zone-based components to absolute positions for the canvas
          if (components.some(c => c.zone)) {
            components = calculateEmailZonePositions(components);
          }
          setTemplate({
            name: templateData.name || '',
            description: templateData.category || 'commercial',
            layout_json: { components },
          });
        }
        setLoading(false);
        return;
      }

      // Form mode: load from /api/templates
      const res = await apiFetch(`${API_URL}/api/templates/${id}`);
      const data = await res.json();
      setTemplate(data);

      // Check both assignments table and direct area field
      if (data.assignments?.length) {
        setSelectedAreas(data.assignments.filter(a => a.area).map(a => a.area));
        setDefaultAreas(data.assignments.filter(a => a.is_default).map(a => a.area));
      } else if (data.area) {
        // Fallback to direct area field if no assignments
        setSelectedAreas([data.area]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComponent = (type, x, y) => {
    // Get default dimensions from mode config
    const typeConfig = COMPONENT_TYPES.find(t => t.id === type);
    const defaultW = typeConfig?.defaultW || (type === 'table' || type === 'data-table' ? 400 : type === 'line' ? 300 : 200);
    const defaultH = typeConfig?.defaultH || (type === 'heading' ? 30 : (type === 'table' || type === 'data-table') ? 100 : type === 'image' ? 80 : type === 'line' ? 2 : 50);

    // Get default content from mode
    let defaultContent = {};
    if (isEmailMode) defaultContent = getEmailDefaultContent(type);
    else if (isMobileMode) defaultContent = getMobileDefaultContent(type);

    const newComp = {
      id: Date.now().toString(),
      type,
      x,
      y,
      width: defaultW,
      height: defaultH,
      content: defaultContent.text || (type === 'text' ? 'Text here' : type === 'heading' ? 'Heading' : ''),
      fontSize: defaultContent.fontSize || (type === 'heading' ? 18 : 12),
      fontWeight: type === 'heading' ? 'bold' : 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      variable: type === 'data-table' ? 'items_table' : '',
      zIndex: 1,
      // Email/mobile mode: store structured content object
      ...(isEmailMode || isMobileMode ? { contentObj: defaultContent } : {}),
      // Table-specific properties
      tableRows: type === 'table' ? 3 : undefined,
      tableCols: type === 'table' ? 3 : undefined,
      tableData: type === 'table' ? [
        ['Header 1', 'Header 2', 'Header 3'],
        ['Row 1 Col 1', 'Row 1 Col 2', 'Row 1 Col 3'],
        ['Row 2 Col 1', 'Row 2 Col 2', 'Row 2 Col 3']
      ] : undefined,
      // Data table specific properties
      dataTablePreset: type === 'data-table' ? 'parts-only' : undefined,
      tableHeaderBgColor: type === 'data-table' ? '#2563eb' : undefined,
      tableHeaderTextColor: type === 'data-table' ? '#ffffff' : undefined
    };

    setTemplate(prev => ({
      ...prev,
      layout_json: { ...prev.layout_json, components: [...prev.layout_json.components, newComp] }
    }));
    setSelectedId(newComp.id);
  };

  const handleUpdateComponent = (id, updates) => {
    setTemplate(prev => ({
      ...prev,
      layout_json: {
        ...prev.layout_json,
        components: prev.layout_json.components.map(c =>
          c.id === id ? { ...c, ...updates } : c
        )
      }
    }));
  };

  // Batch update multiple components at once
  const handleBatchUpdateComponents = (updatesList) => {
    setTemplate(prev => ({
      ...prev,
      layout_json: {
        ...prev.layout_json,
        components: prev.layout_json.components.map(c => {
          const update = updatesList.find(u => u.id === c.id);
          return update ? { ...c, ...update.changes } : c;
        })
      }
    }));
  };

  const handleGroupComponents = (componentIds) => {
    if (componentIds.length < 2) return;

    const groupId = `group-${Date.now()}`;
    const newGroup = {
      id: groupId,
      componentIds: [...componentIds]
    };

    setGroups(prev => [...prev, newGroup]);

    // Mark components as grouped
    setTemplate(prev => ({
      ...prev,
      layout_json: {
        ...prev.layout_json,
        components: prev.layout_json.components.map(c =>
          componentIds.includes(c.id) ? { ...c, groupId } : c
        )
      }
    }));

    // Clear selection
    setSelectedId(null);
    setSelectedIds([]);
  };

  const handleUngroupComponents = (groupId) => {
    // Remove group
    setGroups(prev => prev.filter(g => g.id !== groupId));

    // Unmark components
    setTemplate(prev => ({
      ...prev,
      layout_json: {
        ...prev.layout_json,
        components: prev.layout_json.components.map(c =>
          c.groupId === groupId ? { ...c, groupId: undefined } : c
        )
      }
    }));
  };

  const handleDeleteComponent = (id) => {
    const comp = template.layout_json.components.find(c => c.id === id);
    if (!comp) return;

    if (!window.confirm(`Delete "${comp.type}" component?`)) return;

    setTemplate(prev => ({
      ...prev,
      layout_json: { ...prev.layout_json, components: prev.layout_json.components.filter(c => c.id !== id) }
    }));
    setSelectedId(null);
    setSelectedIds([]);
  };

  // ─── Multi-page support ───
  // Page count = 1 + number of page-break components in the layout.
  const ppmPageCount = (template.layout_json.components || []).filter(c => c.type === 'page-break').length + 1;

  // Add a new page — inserts a page-break component at the boundary between the
  // current last page and the new blank page below it.
  const handleAddPage = () => {
    const PAPER_HEIGHT = PAPER_SIZES[canvasSize]?.height || PAGE_HEIGHT;
    const PAGE_GAP = 20;
    const newY = ppmPageCount * PAPER_HEIGHT + (ppmPageCount - 1) * PAGE_GAP;
    const newComp = {
      id: `pb${Date.now()}`,
      type: 'page-break',
      x: 0,
      y: newY,
      width: PAPER_SIZES[canvasSize]?.width || PAGE_WIDTH,
      height: 4,
      visible: true,
    };
    const newComponents = [...(template.layout_json.components || []), newComp];
    setTemplate({
      ...template,
      layout_json: { ...template.layout_json, components: newComponents },
    });
    setTimeout(() => ppmScrollToPage(ppmPageCount), 100);
  };

  // Scroll the canvas container to a specific page (0-indexed)
  const ppmScrollToPage = (pageIdx) => {
    const container = document.getElementById('ppm-canvas-scroll');
    if (!container) return;
    const PAPER_HEIGHT = PAPER_SIZES[canvasSize]?.height || PAGE_HEIGHT;
    const PAGE_GAP = 20;
    const pageTopPx = pageIdx * (PAPER_HEIGHT + PAGE_GAP) * zoom + 32;
    container.scrollTo({ top: pageTopPx, behavior: 'smooth' });
  };

  const handleSelectComponent = (id, ctrlPressed = false) => {
    if (ctrlPressed && id) {
      // Multi-select mode: toggle selection
      if (selectedIds.includes(id)) {
        // Remove from selection
        const newSelection = selectedIds.filter(sid => sid !== id);
        setSelectedIds(newSelection);
        setSelectedId(newSelection.length > 0 ? newSelection[newSelection.length - 1] : null);
      } else {
        // Add to selection
        const newSelection = [...selectedIds, id];
        setSelectedIds(newSelection);
        setSelectedId(id);
      }
    } else {
      // Single select mode
      setSelectedId(id);
      setSelectedIds(id ? [id] : []);
    }
  };

  const handleSave = async () => {
    if (!template.name.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      setSaving(true);

      // Email mode was retired from Marketing Hub. Newsletter masters are locked
      // HTML files served through /api/marketing/master-templates.
      if (isEmailMode) {
        alert('Marketing newsletter masters are locked. Update the approved master template files and deploy a new version.');
        return;
      }

      // Form mode: save to /api/templates
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew ? `${API_URL}/api/templates` : `${API_URL}/api/templates/${id}`;
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: template.name,
          description: template.description,
          layout_json: template.layout_json
        })
      });
      const saved = await res.json();
      const templateId = saved.id;

      for (const area of selectedAreas) {
        await apiFetch(`${API_URL}/api/templates/${templateId}/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ area, is_default: defaultAreas.includes(area), is_active: true })
        });
      }

      alert('Template saved!');

      // If this was a new template, update URL to edit mode without navigating away
      if (isNew) {
        window.history.replaceState({}, '', `/template-builder/${templateId}`);
      }

      // Don't navigate away - stay in designer
    } catch (err) {
      console.error(err);
      alert('Error saving');
    } finally {
      setSaving(false);
    }
  };

  // Export the current template as a portable JSON file for the PPM library
  // on the sm8-addons ADG portal. Downloads a .ppm-template.json file that can
  // be imported via the PPM Template Builder's 📚 Library → Import button.
  const handleExportPpm = () => {
    if (!template.name || !template.name.trim()) {
      alert('Please name the template before exporting.');
      return;
    }
    const exportObj = {
      format: 'ppm-template',
      version: 1,
      exportedAt: new Date().toISOString(),
      source: 'doorbase',
      name: template.name,
      description: template.description || '',
      layout_json: template.layout_json,
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = template.name.replace(/[^a-z0-9]/gi, '_');
    a.download = `${safeName}.ppm-template.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert(`Template "${template.name}" exported.\n\nTo use it on the ADG portal:\n1. Open PPM → Template Builder\n2. Click 📚 Library\n3. Click Import and upload this file`);
  };

  const handleSaveAs = async (newName) => {
    try {
      setSaving(true);
      // Always POST for Save As (creates new template)
      const res = await apiFetch(`${API_URL}/api/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName,
          description: template.description,
          layout_json: template.layout_json
        })
      });
      const saved = await res.json();
      const templateId = saved.id;

      // Copy area assignments
      for (const area of selectedAreas) {
        await apiFetch(`${API_URL}/api/templates/${templateId}/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ area, is_default: defaultAreas.includes(area), is_active: true })
        });
      }

      alert(`Template saved as "${newName}"!`);

      // Navigate to the new template
      navigate(`/template-builder/${templateId}`);
    } catch (err) {
      console.error(err);
      alert('Error saving template');
    } finally {
      setSaving(false);
    }
  };

  const togglePreview = () => {
    if (!previewMode) {
      // Switching to preview mode - generate HTML
      let html;
      if (isEmailMode) {
        // Email mode: use table-based email HTML renderer
        html = componentsToEmailHtml(template.layout_json.components, template.name || 'Email Preview');
      } else {
        // Form/PDF mode: use absolute-positioned renderer with sample data
        const sampleData = getSampleDataForArea(selectedAreas[0]);
        html = renderTemplateWithData(template.layout_json, sampleData);
      }
      setPreviewHtml(html);
    }
    setPreviewMode(!previewMode);
  };

  const getSampleDataForArea = (area) => {
    const samples = {
      sales_order: {
        customer_name: 'ACME Corporation Ltd',
        customer_address: '123 Business Park\nColchester\nEssex\nCO1 1AA',
        customer_po: 'PO-2024-001',
        order_number: 'SO-001',
        order_date: new Date().toLocaleDateString(),
        expected_dispatch: new Date(Date.now() + 7*24*60*60*1000).toLocaleDateString(),
        delivery_address: 'Warehouse 5\n45 Industrial Estate\nHarwich\nEssex\nCO12 3XY',
        delivery_method: 'Next Day Delivery',
        delivery_contact: 'John Smith',
        delivery_phone: '01206 987654',
        delivery_instructions: 'Deliver to loading bay 3',
        customer_notes: 'Please include installation instructions',
        notes: 'Internal notes: Customer is priority account',
        items: [
          { product_code: 'DOOR-001', description: 'Commercial Fire Door', quantity: 2, unit_price: '450.00', total: '900.00' },
          { product_code: 'CLOSER-HC', description: 'Heavy Duty Door Closer', quantity: 2, unit_price: '85.00', total: '170.00' }
        ],
        subtotal: '1,070.00',
        vat: '214.00',
        total: '1,284.00',
        company_name: companyName || 'DoorBase Ltd',
        company_logo: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="80" viewBox="0 0 200 80"%3E%3Crect width="200" height="80" fill="%232563eb"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="24" fill="white" text-anchor="middle" dominant-baseline="middle"%3EDoorBase%3C/text%3E%3C/svg%3E',
        company_address: '1, Riverside Ave West\nLawford\nManningtree\nEssex. CO11 1UN',
        company_address_line1: '1 Riverside Avenue West',
        company_address_line2: 'Lawford Industrial Estate',
        company_city: 'Manningtree',
        company_county: 'Essex',
        company_postcode: 'CO11 2HS',
        company_phone: '01206 123456',
        company_email: 'sales@doorbase.co.uk',
        company_phone_formatted: 'TEL: 01206 123456',
        company_email_formatted: 'Email: sales@doorbase.co.uk'
      },
      quote: {
        customer_name: 'Sample Customer Ltd',
        quote_number: 'Q-001',
        quote_date: new Date().toLocaleDateString(),
        items: [{ description: 'Sample Product', quantity: 1, unit_price: '100.00', total: '100.00' }],
        subtotal: '100.00',
        vat_amount: '20.00',
        total: '120.00'
      },
      ppm_quote: {
        customer_name: 'Tesco Stores Ltd',
        contact_name: 'Mr David Smith',
        site_address: 'Tesco Extra, Copdock Interchange\nIpswich, Suffolk\nIP8 3SH',
        customer_email: 'david.smith@tesco.com',
        customer_phone: '01473 200000',
        customer_uuid: '',
        quote_ref: '2001',
        quote_date: new Date().toLocaleDateString('en-GB'),
        variant: 'new',
        contract_length: '3',
        visits_per_year: '2',
        valid_until: new Date(Date.now() + 30*86400000).toLocaleDateString('en-GB'),
        quote_status: 'Quoted',
        auto_doors: '4',
        manual_doors: '2',
        gates: '1',
        barriers: '0',
        shutters: '1',
        total_assets: '8',
        asset_details: '4x Auto Doors, 2x Manual, 1x Gate, 1x Shutter',
        per_visit: '£317.50',
        annual_value: '£635.00',
        contract_value: '£1,905.00',
        discount_percent: '5%',
        subtotal: '£1,905.00',
        vat_amount: '£381.00',
        total: '£2,286.00',
        payment_terms: 'Net 30 days from invoice date',
        callout_standard: '£220',
        callout_response: '£80',
        callout_emergency: '£315',
        callout_out_of_hours: '£395',
        company_name: 'Automated Doors & Gates Ltd',
        company_logo: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="80" viewBox="0 0 200 80"%3E%3Crect width="200" height="80" fill="%23002070"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="20" fill="white" text-anchor="middle" dominant-baseline="middle"%3EADG%3C/text%3E%3C/svg%3E',
        company_address: '50 Princes Street, Ipswich, IP1 1RJ',
        company_address_line1: '50 Princes Street',
        company_address_line2: '',
        company_city: 'Ipswich',
        company_county: 'Suffolk',
        company_postcode: 'IP1 1RJ',
        company_phone: '01206 591001',
        company_mobile: '07814 000000',
        company_email: 'service@a-d-g.co.uk',
        company_website: 'www.a-d-g.co.uk',
        company_vat_number: 'GB 921 3456 78',
        company_number: '05803042',
        company_phone_formatted: 'Tel: 01206 591001',
        company_email_formatted: 'Email: service@a-d-g.co.uk',
        company_mobile_formatted: 'Mobile: 07814 000000',
        today_date: new Date().toLocaleDateString('en-GB'),
        contract_start: new Date(Date.now() + 14*86400000).toLocaleDateString('en-GB'),
        contract_end: new Date(Date.now() + 3*365*86400000).toLocaleDateString('en-GB'),
        next_visit_date: new Date(Date.now() + 30*86400000).toLocaleDateString('en-GB'),
        feature_image: '',
        intro_heading: 'Planned Preventive Maintenance Quotation',
        intro_body: 'We are pleased to provide the following quotation for the planned preventive maintenance of your equipment, ensuring compliance with current health and safety legislation.',
        about_body: 'Automated Doors & Gates Ltd has over 25 years experience servicing and installing automatic doors, gates, and barriers across East Anglia. All our engineers are fully qualified and insured.',
        scope_body: 'Our PPM service includes: bi-annual inspection and service of all automated equipment; safety device testing; lubrication and adjustment; compliance certification.',
        scope_items: '2x service visits per year\nSafety device testing\nCompliance certification',
        terms_list: '1. Quotation valid for 30 days\n2. Prices exclude VAT\n3. Payment terms: Net 30 days\n4. Contract minimum term 12 months',
        signoff_text: 'We look forward to servicing your equipment.',
        signoff_name: 'Service Manager',
        signoff_dept: 'Service Department',
        page_number: '1',
        total_pages: '1'
      }
    };
    return samples[area] || samples.sales_order;
  };

  const renderTemplateWithData = (layout, data) => {
    if (!layout || !layout.components) return '<p>No template content</p>';

    let html = '<div style="width: 794px; position: relative; background: white; padding: 20px; min-height: 1123px;">';

    layout.components.forEach(comp => {
      let content = comp.content || '';

      // Build base style with background color if set
      const backgroundColor = comp.backgroundColor || 'transparent';

      // Handle different component types
      if (comp.type === 'image') {
        // Image component - check uploaded image first, then variable
        let imgSrc = '';

        if (comp.uploadedImage) {
          // Use uploaded image if available
          imgSrc = comp.uploadedImage;
        } else if (comp.variable && data[comp.variable]) {
          // Use variable data (e.g., company_logo)
          imgSrc = data[comp.variable];
        }

        if (imgSrc) {
          const fitStyle = comp.objectFit || 'contain';
          const alignStyle = comp.imageAlign === 'left' ? 'flex-start' : comp.imageAlign === 'right' ? 'flex-end' : 'center';
          const style = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; height: ${comp.height}px; background-color: ${backgroundColor}; display: flex; justify-content: ${alignStyle}; align-items: center;`;
          html += `<div style="${style}"><img src="${imgSrc}" style="width: 100%; height: 100%; object-fit: ${fitStyle};" alt="Image" /></div>`;
        } else {
          // Empty image placeholder
          const style = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; height: ${comp.height}px; background-color: ${backgroundColor}; border: 2px dashed #ccc; display: flex; align-items: center; justify-content: center; color: #999;`;
          html += `<div style="${style}">No image</div>`;
        }
        return;
      }

      if (comp.type === 'line') {
        // Line component
        const style = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; height: ${comp.height}px; border-top: 2px solid #333;`;
        html += `<div style="${style}"></div>`;
        return;
      }

      if (comp.type === 'spacer') {
        // Spacer component
        const style = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; height: ${comp.height}px; background-color: ${backgroundColor};`;
        html += `<div style="${style}"></div>`;
        return;
      }

      if (comp.type === 'table') {
        // Custom table component
        const tableData = comp.tableData || [['Cell']];
        const headerBgColor = comp.tableHeaderBgColor || '#f3f4f6';
        const headerTextColor = comp.tableHeaderTextColor || '#000000';
        const cellStyles = comp.tableCellStyles || {};

        const getCellStyle = (rowIdx, colIdx) => {
          const key = `${rowIdx}-${colIdx}`;
          const custom = cellStyles[key] || {};
          const isHeader = rowIdx === 0;

          const styles = [];
          styles.push('border: 1px solid #ddd');
          styles.push('padding: 4px');
          styles.push(`font-size: ${custom.fontSize || 10}px`);
          styles.push(`font-weight: ${custom.fontWeight || (isHeader ? 'bold' : 'normal')}`);
          styles.push(`font-style: ${custom.fontStyle || 'normal'}`);
          styles.push(`text-align: ${custom.textAlign || 'left'}`);
          styles.push(`color: ${custom.color || (isHeader ? headerTextColor : '#000000')}`);

          const bgColor = custom.backgroundColor || (isHeader ? headerBgColor : 'transparent');
          if (bgColor && bgColor !== 'transparent') {
            styles.push(`background-color: ${bgColor}`);
          }

          return styles.join('; ');
        };

        const containerStyle = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; height: ${comp.height}px; overflow: hidden;`;
        let tableHtml = `<div style="${containerStyle}">`;
        tableHtml += '<table style="width: 100%; border-collapse: collapse;">';
        tableData.forEach((row, rowIdx) => {
          tableHtml += '<tr>';
          row.forEach((cell, colIdx) => {
            tableHtml += `<td style="${getCellStyle(rowIdx, colIdx)}">${cell}</td>`;
          });
          tableHtml += '</tr>';
        });
        tableHtml += '</table></div>';
        html += tableHtml;
        return;
      }

      if (comp.type === 'data-table') {
        // Data table - renders items_table with preset columns
        const preset = comp.dataTablePreset || 'parts-only';
        const headerBg = comp.tableHeaderBgColor || '#2563eb';
        const headerText = comp.tableHeaderTextColor || '#ffffff';

        const containerStyle = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; min-height: ${comp.height}px;`;
        let tableHtml = `<div style="${containerStyle}">`;
        tableHtml += '<table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">';

        // Header row based on preset
        tableHtml += `<thead><tr style="background-color: ${headerBg}; color: ${headerText};">`;
        if (preset === 'parts-only') {
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold;">Part No</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold;">Description</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold;">Qty</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold;">Price</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold;">Total</th>';
        } else if (preset === 'parts-labor') {
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold;">Description</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold;">Qty/Hours</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold;">Rate</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold;">Total</th>';
        } else {
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold;">Item</th>';
          tableHtml += '<th style="border: 1px solid #ddd; padding: 6px; text-align: left; font-weight: bold;">Details</th>';
        }
        tableHtml += '</tr></thead>';

        // Data rows from items
        tableHtml += '<tbody>';
        if (data.items && Array.isArray(data.items)) {
          data.items.forEach((item, idx) => {
            const rowBg = idx % 2 === 0 ? '#ffffff' : '#f9fafb';
            tableHtml += `<tr style="background-color: ${rowBg};">`;

            if (preset === 'parts-only') {
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px;">${item.product_code || ''}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px;">${item.description || ''}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${item.quantity || ''}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px; text-align: right;">£${item.unit_price || '0.00'}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px; text-align: right;">£${item.total || '0.00'}</td>`;
            } else if (preset === 'parts-labor') {
              const qtyDisplay = item.quantity ? item.quantity : (item.hours ? `${item.hours} hrs` : '');
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px;">${item.description || ''}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${qtyDisplay}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px; text-align: right;">£${item.unit_price || item.rate || '0.00'}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px; text-align: right;">£${item.total || '0.00'}</td>`;
            } else {
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px;">${item.name || item.description || ''}</td>`;
              tableHtml += `<td style="border: 1px solid #ddd; padding: 6px;">${item.details || ''}</td>`;
            }

            tableHtml += '</tr>';
          });
        }
        tableHtml += '</tbody>';
        tableHtml += '</table></div>';
        html += tableHtml;
        return;
      }

      // Replace variables for text/variable components
      if (comp.variable && data[comp.variable]) {
        if (comp.variable === 'items' && Array.isArray(data.items)) {
          // Table rendering
          content = '<table style="width:100%; border-collapse: collapse; border: 1px solid #ccc;">';
          content += '<thead><tr style="background: #f0f0f0;"><th style="border: 1px solid #ccc; padding: 8px; text-align: left;">Product</th><th style="border: 1px solid #ccc; padding: 8px; text-align: left;">Description</th><th style="border: 1px solid #ccc; padding: 8px; text-align: right;">Qty</th><th style="border: 1px solid #ccc; padding: 8px; text-align: right;">Price</th><th style="border: 1px solid #ccc; padding: 8px; text-align: right;">Total</th></tr></thead>';
          content += '<tbody>';
          data.items.forEach(item => {
            content += `<tr><td style="border: 1px solid #ccc; padding: 8px;">${item.product_code || ''}</td><td style="border: 1px solid #ccc; padding: 8px;">${item.description}</td><td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${item.quantity}</td><td style="border: 1px solid #ccc; padding: 8px; text-align: right;">£${item.unit_price}</td><td style="border: 1px solid #ccc; padding: 8px; text-align: right;">£${item.total}</td></tr>`;
          });
          content += '</tbody></table>';
        } else {
          content = data[comp.variable];
        }
      }

      // Text/heading/variable components with background color and text color
      const textColor = comp.color || '#000000';
      const fontFamily = comp.fontFamily || 'Arial';
      const lineHeight = comp.lineHeight || 1.2;
      const letterSpacing = comp.letterSpacing || 0;
      const paddingY = comp.paddingY !== undefined ? comp.paddingY : 4;
      const paddingX = comp.paddingX !== undefined ? comp.paddingX : 4;
      const style = `position: absolute; left: ${comp.x}px; top: ${comp.y}px; width: ${comp.width}px; height: ${comp.height}px; font-size: ${comp.fontSize}px; font-family: ${fontFamily}; font-weight: ${comp.fontWeight || 'normal'}; font-style: ${comp.fontStyle || 'normal'}; text-decoration: ${comp.textDecoration || 'none'}; text-align: ${comp.textAlign || 'left'}; line-height: ${lineHeight}; letter-spacing: ${letterSpacing}px; white-space: pre-line; background-color: ${backgroundColor}; color: ${textColor}; padding: ${paddingY}px ${paddingX}px; box-sizing: border-box;`;

      html += `<div style="${style}">${content}</div>`;
    });

    html += '</div>';
    return html;
  };

  const toggleArea = (area) => {
    if (selectedAreas.includes(area)) {
      setSelectedAreas(selectedAreas.filter(a => a !== area));
      setDefaultAreas(defaultAreas.filter(a => a !== area));
    } else {
      setSelectedAreas([...selectedAreas, area]);
      // Auto-switch to PPM mode when PPM Quotes is ticked (gives access to
      // the full PPM component palette: asset table, pricing, callout rates,
      // terms, acceptance form, etc.)
      if (area === 'ppm_quote' && mode !== PPM_QUOTE_MODE) {
        setMode(PPM_QUOTE_MODE);
      }
    }
  };

  const toggleDefault = (area) => {
    setDefaultAreas(prev => prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]);
  };

  const areas = [
    { value: 'quote', label: 'Quotes' },
    { value: 'invoice', label: 'Invoices' },
    { value: 'job', label: 'Jobs' },
    { value: 'sales_order', label: 'Sales Orders' },
    { value: 'delivery_note', label: 'Delivery Notes' },
    { value: 'ppm_contract', label: 'PPM Contracts' },
    { value: 'ppm_quote', label: 'PPM Quotes' }
  ];

  const getVars = () => selectedAreas.length ? (AREA_VARIABLES[selectedAreas[0]] || {}) : {};
  const selected = template.layout_json.components?.find(c => c.id === selectedId);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-30" style={{ height: '116px' }}>
          <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-between py-3">
            {/* Company Name - Top Left */}
            {companyName && (
              <div className="text-white text-sm font-medium opacity-90">
                {companyName}
              </div>
            )}

            {/* Main Title Area */}
            <div className="flex items-center justify-between flex-1">
              <button
                onClick={() => {
                  if (isEmailMode && onClose) onClose();
                  else navigate('/template-library');
                }}
                className="text-white hover:text-blue-100 transition text-lg font-semibold z-10"
                title={isEmailMode ? 'Back to Templates' : 'Back to Template Library'}
              >
                ⬅ {isEmailMode ? 'Email Templates' : 'Template Library'}
              </button>
              <div className="absolute left-1/2 transform -translate-x-1/2 text-center">
                <h1 className="text-4xl font-bold">{isNew ? 'New Template' : 'Template Editor'}</h1>
                <span className="text-sm text-blue-100">{isEmailMode ? 'Email Designer' : 'Professional WYSIWYG Designer'}</span>
              </div>
              <div className="flex gap-2 items-center ml-auto z-10">
                {/* Zoom Controls */}
                {!previewMode && (
                  <div className="flex items-center gap-1 bg-white bg-opacity-20 rounded-lg px-2 py-1">
                    <button
                      onClick={() => setZoom(Math.max(0.25, zoom - 0.1))}
                      className="px-2 py-1 bg-white bg-opacity-30 hover:bg-opacity-50 rounded text-white font-bold transition"
                      title="Zoom Out"
                    >
                      −
                    </button>
                    <span className="px-3 text-sm font-medium text-white min-w-[60px] text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                      className="px-2 py-1 bg-white bg-opacity-30 hover:bg-opacity-50 rounded text-white font-bold transition"
                      title="Zoom In"
                    >
                      +
                    </button>
                    <button
                      onClick={() => setZoom(1)}
                      className="px-2 py-1 bg-white bg-opacity-30 hover:bg-opacity-50 rounded text-white text-xs transition ml-1"
                      title="Reset to 100%"
                    >
                      100%
                    </button>
                  </div>
                )}

                <button
                  onClick={togglePreview}
                  className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
                    previewMode
                      ? 'bg-gray-300 hover:bg-gray-400 text-black'
                      : 'bg-gray-500 hover:bg-gray-600 text-white'
                  }`}
                >
                  {previewMode ? '👁️ Preview Mode' : '✏️ Design Mode'}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm disabled:opacity-50"
                >
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
                {!isNew && (
                  <button
                    onClick={() => {
                      const newName = prompt('Enter new template name:', template.name + ' (Copy)');
                      if (newName && newName.trim()) {
                        handleSaveAs(newName.trim());
                      }
                    }}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm disabled:opacity-50"
                  >
                    📋 Save As
                  </button>
                )}
                {selectedAreas.includes('ppm_quote') && (
                  <button
                    onClick={handleExportPpm}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition text-sm"
                    title="Export this template for the PPM library on the ADG portal"
                  >
                    📤 Export PPM
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-[calc(100vh-72px)]">
          <div className="w-80 bg-white border-r overflow-y-auto">
            <div className="p-4 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Template Info</h3>
                <input
                  type="text"
                  placeholder="Name"
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded mb-2 text-sm"
                />
                <textarea
                  placeholder="Description"
                  value={template.description || ''}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm"
                  rows="2"
                />
              </div>

              {!isEmailMode && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Assign To</h3>
                <div className="space-y-1">
                  {areas.map(a => (
                    <div key={a.value} className="flex items-center justify-between">
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedAreas.includes(a.value)}
                          onChange={() => toggleArea(a.value)}
                          className="rounded"
                        />
                        <span>{a.label}</span>
                      </label>
                      {selectedAreas.includes(a.value) && (
                        <button
                          onClick={() => toggleDefault(a.value)}
                          className={`text-xs px-2 py-1 rounded ${
                            defaultAreas.includes(a.value) ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {defaultAreas.includes(a.value) ? '★ Default' : 'Set Default'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              )}

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Import</h3>
                <button
                  onClick={() => document.getElementById('pdf-upload-input').click()}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded font-medium transition flex items-center justify-center space-x-2 mb-4"
                >
                  <span>📄</span>
                  <span>Import from PDF</span>
                </button>
                <input
                  id="pdf-upload-input"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    try {
                      const formData = new FormData();
                      formData.append('pdf', file);
                      const res = await apiFetch(`${API_URL}/api/template-analyzer/analyze-pdf`, {
                        method: 'POST',
                        body: formData
                      });
                      const result = await res.json();

                      if (result.success) {
                        setTemplate(prev => ({
                          ...prev,
                          name: prev.name || `Imported from ${file.name}`,
                          layout_json: { components: result.analysis.suggestedComponents }
                        }));

                        if (result.analysis.suggestedArea) {
                          setSelectedAreas([result.analysis.suggestedArea]);
                        }

                        alert(`Template imported!\nType: ${result.analysis.suggestedArea || 'Unknown'}\nConfidence: ${Math.round(result.analysis.confidence * 100)}%`);
                      } else {
                        alert('Analysis failed: ' + (result.error || 'Unknown error'));
                      }
                    } catch (err) {
                      alert('Error analyzing PDF: ' + err.message);
                    }
                    e.target.value = '';
                  }}
                />
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Components</h3>
                <p className="text-xs text-gray-500 mb-3">Drag onto canvas</p>
                <div className="space-y-2">
                  {COMPONENT_TYPES.map(t => <PaletteItem key={t.id} type={t} />)}
                </div>
              </div>

            </div>
          </div>

          <div className="flex-1 flex flex-col bg-gray-200">
            {previewMode ? (
              <div className="flex justify-center p-8">
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            ) : (
              <>
                <AlignmentToolbar
                  components={template.layout_json.components || []}
                  selectedIds={selectedIds}
                  onUpdate={handleUpdateComponent}
                  onBatchUpdate={handleBatchUpdateComponents}
                />
                {/* Page management toolbar — multi-page support */}
                <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">📄 Pages: {ppmPageCount}</span>
                  <div className="flex gap-1">
                    {Array.from({ length: ppmPageCount }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => ppmScrollToPage(i)}
                        className="px-2 py-1 text-xs bg-white border border-blue-600 text-blue-600 rounded hover:bg-blue-50 transition"
                        title={`Go to page ${i + 1}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={handleAddPage}
                    className="ml-auto px-3 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 transition"
                    title="Add a new page"
                  >
                    + Add Page
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-8" id="ppm-canvas-scroll">
                  <Canvas
                    components={template.layout_json.components || []}
                    onAddComponent={handleAddComponent}
                    onUpdateComponent={handleUpdateComponent}
                    onBatchUpdateComponents={handleBatchUpdateComponents}
                    onDeleteComponent={handleDeleteComponent}
                    selectedId={selectedId}
                    selectedIds={selectedIds}
                    onSelectComponent={handleSelectComponent}
                    zoom={zoom}
                    snapToGridEnabled={snapToGridEnabled}
                    showGrid={showGrid}
                    gridSize={gridSize}
                    canvasSize={canvasSize}
                    snapPreview={snapPreview}
                    setSnapPreview={setSnapPreview}
                    showBackgrounds={showBackgrounds}
                    isEmailMode={isEmailMode}
                  />
                </div>
              </>
            )}
          </div>

          <div className="w-80 bg-white border-l flex flex-col">
            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setRightPanelTab('properties')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  rightPanelTab === 'properties'
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Properties
              </button>
              <button
                onClick={() => setRightPanelTab('layers')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition relative ${
                  rightPanelTab === 'layers'
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Layers
                <span className="ml-1 text-xs bg-gray-200 px-1.5 py-0.5 rounded">
                  {template.layout_json.components?.length || 0}
                </span>
              </button>
              <button
                onClick={() => setRightPanelTab('image')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${rightPanelTab === 'image' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
              >
                🖼️ Image
              </button>
              <button
                onClick={() => setRightPanelTab('settings')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${rightPanelTab === 'settings' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
              >
                Settings
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              {rightPanelTab === 'properties' && (
                <PropertiesPanel
                  component={selected}
                  onUpdate={handleUpdateComponent}
                  availableVariables={getVars()}
                  setShowTableEditor={setShowTableEditor}
                  />
              )}

              {rightPanelTab === 'layers' && (
                <LayersPanel
                  components={template.layout_json.components || []}
                  selectedId={selectedId}
                  selectedIds={selectedIds}
                  onSelect={handleSelectComponent}
                  onUpdate={handleUpdateComponent}
                  onDelete={handleDeleteComponent}
                />
              )}

              {rightPanelTab === 'image' && (
                <div className="p-4 space-y-4">
                  {/* ── Selected Component Image ── */}
                  {selected && (selected.type === 'image' || selected.type === 'header') && (
                    <div className="border rounded p-3 bg-gray-50">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">
                        {selected.type === 'header' ? 'Header Logo' : 'Selected Image'}
                      </h4>
                      {selected.type === 'header' && (() => {
                        const co = selected.contentObj || {};
                        const updateCO = (key, val) => handleUpdateComponent(selected.id, { contentObj: { ...co, [key]: val } });
                        return (
                          <div className="space-y-2">
                            <input type="text" value={co.logoUrl || ''} onChange={e => updateCO('logoUrl', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" placeholder="Paste URL or Use from library below" />
                            {co.logoUrl && (
                              <div className="flex items-center gap-2 p-1 bg-white rounded border">
                                <img src={co.logoUrl} alt="Logo" className="h-8 object-contain" />
                                <span className="text-xs text-gray-600 truncate flex-1">{co.logoLabel || 'Logo'}</span>
                                <button onClick={() => { updateCO('logoUrl', ''); updateCO('logoLabel', ''); }} className="text-xs text-red-500 hover:text-red-700">✕</button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {selected.type === 'image' && (
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">Image Source</label>
                          <select
                            value={selected.imageSource || 'variable'}
                            onChange={(e) => handleUpdateComponent(selected.id, { imageSource: e.target.value })}
                            className="w-full px-2 py-1 border rounded text-sm"
                          >
                            <option value="variable">Dynamic Variable</option>
                            <option value="upload">Upload File</option>
                            <option value="library">Image Library</option>
                          </select>
                          {(selected.imageSource === 'variable' || !selected.imageSource) && (
                            <select
                              value={selected.variable || ''}
                              onChange={(e) => handleUpdateComponent(selected.id, { variable: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm"
                            >
                              <option value="">Select variable...</option>
                              <option value="company_logo">Company Logo</option>
                            </select>
                          )}
                          {selected.imageSource === 'upload' && (
                            <>
                              <label className="block">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const img = new Image();
                                    const reader = new FileReader();
                                    reader.onload = (ev) => {
                                      const dataUrl = ev.target.result;
                                      img.src = dataUrl;
                                      img.onload = () => {
                                        let w = img.width, h = img.height;
                                        if (w > 800 || h > 600) {
                                          const r = Math.min(800 / w, 600 / h);
                                          w = w * r; h = h * r;
                                          const canvas = document.createElement('canvas');
                                          canvas.width = w; canvas.height = h;
                                          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                                          handleUpdateComponent(selected.id, { uploadedImage: canvas.toDataURL('image/jpeg', 0.85) });
                                        } else {
                                          handleUpdateComponent(selected.id, { uploadedImage: dataUrl });
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    };
                                    reader.readAsDataURL(file);
                                  }}
                                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                              </label>
                              {selected.uploadedImage && (
                                <div className="mt-2">
                                  <div className="border rounded p-2 bg-white">
                                    <img src={selected.uploadedImage} alt="Preview" className="w-full h-auto max-h-32 object-contain" />
                                  </div>
                                  <button onClick={() => handleUpdateComponent(selected.id, { uploadedImage: null, imageSource: 'variable' })} className="mt-1 w-full bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200">Remove Image</button>
                                </div>
                              )}
                            </>
                          )}
                          {selected.imageSource === 'library' && !selected.uploadedImage && (
                            <p className="text-xs text-gray-500 italic">Click Use on a library image below</p>
                          )}
                          {selected.imageSource === 'library' && selected.uploadedImage && (
                            <div className="mt-2">
                              <div className="border rounded p-1 bg-white">
                                <img src={selected.src || selected.uploadedImage} alt="Selected" className="w-full object-contain" style={{ maxHeight: '80px' }} />
                              </div>
                              <button onClick={() => handleUpdateComponent(selected.id, { src: '', uploadedImage: null, imageSource: 'variable' })} className="mt-1 w-full bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200">Remove</button>
                            </div>
                          )}
                          {/* Image positioning */}
                          <div className="border-t pt-2 mt-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Fit</label>
                            <select
                              value={selected.objectFit || 'contain'}
                              onChange={(e) => handleUpdateComponent(selected.id, { objectFit: e.target.value })}
                              className="w-full px-2 py-1 border rounded text-sm"
                            >
                              <option value="contain">Contain (fit inside)</option>
                              <option value="cover">Cover (fill box)</option>
                              <option value="fill">Fill (stretch)</option>
                              <option value="none">None (original size)</option>
                            </select>
                            <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">Align</label>
                            <div className="flex gap-1">
                              {['left', 'center', 'right'].map(a => (
                                <button key={a} onClick={() => handleUpdateComponent(selected.id, { imageAlign: a })} className={`flex-1 px-2 py-1 text-xs rounded ${(selected.imageAlign || 'center') === a ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>{a}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!selected && (
                    <div className="border rounded p-3 bg-gray-50 text-center">
                      <p className="text-xs text-gray-500">Select an image or header component to manage its image</p>
                    </div>
                  )}

                  {/* ── Image Library ── */}
                  <div className="border-t pt-3">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Image Library</h4>
                    <label className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded text-sm font-medium border border-blue-200 flex items-center justify-center gap-2 cursor-pointer mb-2">
                      <span>📤</span> Upload Image
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const formData = new FormData();
                            formData.append('image', file);
                            formData.append('label', file.name.replace(/\.[^.]+$/, ''));
                            const token = localStorage.getItem('doorbase_token');
                            const res = await fetch(`${API_URL}/api/marketing/images`, {
                              method: 'POST',
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                              body: formData,
                            });
                            if (!res.ok) { const err = await res.json(); alert(err.error || 'Upload failed'); return; }
                            await loadLibraryImages();
                          } catch (err) { console.error('Upload error:', err); alert(err.message || 'Upload failed'); }
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="hidden"
                      />
                    </label>

                    {libraryLoading && <p className="text-xs text-gray-400 text-center">Loading...</p>}

                    {libraryImages.length > 0 && (
                      <div className="grid grid-cols-2 gap-1.5">
                        {libraryImages.map(img => (
                          <div key={img.filename} className="group border rounded bg-white overflow-hidden">
                            <img src={img.url} alt={img.label} className="w-full aspect-square object-contain p-1 bg-gray-50" />
                            <div className="px-1.5 py-1">
                              {editingLabel === img.filename ? (
                                <input
                                  type="text"
                                  value={editLabelValue}
                                  onChange={e => setEditLabelValue(e.target.value)}
                                  onKeyDown={async (e) => {
                                    if (e.key === 'Enter') {
                                      try {
                                        await apiFetch(`${API_URL}/api/marketing/images/${encodeURIComponent(img.filename)}/rename`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ label: editLabelValue }),
                                        });
                                        setLibraryImages(prev => prev.map(i => i.filename === img.filename ? { ...i, label: editLabelValue } : i));
                                      } catch (err) { console.error('Rename error:', err); }
                                      setEditingLabel(null);
                                    }
                                    if (e.key === 'Escape') setEditingLabel(null);
                                  }}
                                  className="w-full px-1 py-0.5 border rounded text-xs"
                                  autoFocus
                                />
                              ) : (
                                <p className="text-[10px] font-medium text-gray-800 truncate" title={img.label}>{img.label || img.filename}</p>
                              )}
                              <p className="text-[9px] text-gray-400">{(img.size / 1024).toFixed(0)} KB</p>
                              <div className="flex gap-0.5 mt-1 flex-wrap">
                                <button
                                  onClick={() => {
                                    if (!selected) {
                                      alert('Select a component first (header or image), then click Use.');
                                      return;
                                    }
                                    const co = selected.contentObj || {};
                                    if (selected.type === 'header') {
                                      handleUpdateComponent(selected.id, { contentObj: { ...co, logoUrl: img.url, logoLabel: img.label || '' } });
                                    } else if (selected.type === 'image') {
                                      handleUpdateComponent(selected.id, { contentObj: { ...co, src: img.url }, uploadedImage: img.url, imageSource: 'library' });
                                    } else {
                                      alert('Select a header or image component to use this image.');
                                    }
                                  }}
                                  className="text-[10px] px-1.5 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200"
                                >Use</button>
                                <button
                                  onClick={() => navigator.clipboard.writeText(img.url)}
                                  className="text-[10px] px-1.5 py-0.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border border-gray-200"
                                >Copy</button>
                                {editingLabel !== img.filename && (
                                  <button
                                    onClick={() => { setEditingLabel(img.filename); setEditLabelValue(img.label || ''); }}
                                    className="text-[10px] px-1.5 py-0.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border border-gray-200"
                                  >Rename</button>
                                )}
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Delete this image?')) return;
                                    try {
                                      await apiFetch(`${API_URL}/api/marketing/images/${encodeURIComponent(img.filename)}`, { method: 'DELETE' });
                                      setLibraryImages(prev => prev.filter(i => i.filename !== img.filename));
                                    } catch (err) { console.error('Delete error:', err); alert('Delete failed'); }
                                  }}
                                  className="text-[10px] px-1.5 py-0.5 bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
                                >Del</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {libraryImages.length === 0 && !libraryLoading && (
                      <p className="text-xs text-gray-400 text-center">No images uploaded yet</p>
                    )}
                  </div>
                </div>
              )}

              {rightPanelTab === 'settings' && (
                <SettingsPanel
                  snapToGridEnabled={snapToGridEnabled}
                  setSnapToGridEnabled={setSnapToGridEnabled}
                  showGrid={showGrid}
                  setShowGrid={setShowGrid}
                  gridSize={gridSize}
                  setGridSize={setGridSize}
                  zoom={zoom}
                  setZoom={setZoom}
                  canvasSize={canvasSize}
                  setCanvasSize={setCanvasSize}
                  showBackgrounds={showBackgrounds}
                  setShowBackgrounds={setShowBackgrounds}
                  handleUndo={handleUndo}
                  handleRedo={handleRedo}
                  history={history}
                  historyIndex={historyIndex}
                  selectedIds={selectedIds}
                  components={template.layout_json.components || []}
                  onBatchUpdate={handleBatchUpdateComponents}
                  onGroupComponents={handleGroupComponents}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Editor Modal */}
      {showTableEditor && selected && selected.type === 'table' && (
        <TableEditorModal
          component={selected}
          onUpdate={handleUpdateComponent}
          onClose={() => setShowTableEditor(false)}
        />
      )}

    </DndProvider>
  );
}

export default TemplateBuilder;
